import { createFileRoute, Link } from '@tanstack/react-router';
import {
    ArrowLeft,
    ArrowUp,
    Check,
    ChevronDown,
    ChevronRight,
    Copy,
    Loader2,
    Pencil,
    RefreshCw,
    Trash2,
    XCircle,
} from 'lucide-react';
import React, {
    useCallback,
    useDeferredValue,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import { MessageItem } from '@apps/web/components/shared/messages/message_item';
import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import { Checkbox } from '@apps/web/components/ui/checkbox';
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from '@apps/web/components/ui/collapsible';
import { Combobox } from '@apps/web/components/ui/combobox';
import {
    CommandEmpty,
    CommandGroup,
    CommandItem,
} from '@apps/web/components/ui/command';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@apps/web/components/ui/dialog';
import { Input } from '@apps/web/components/ui/input';
import { Label } from '@apps/web/components/ui/label';
import { Markdown } from '@apps/web/components/ui/markdown';
import {
    ResizableHandle,
    ResizablePanel,
    ResizablePanelGroup,
} from '@apps/web/components/ui/resizable';
import { ScrollArea } from '@apps/web/components/ui/scroll_area';
import { Separator } from '@apps/web/components/ui/separator';
import { Textarea } from '@apps/web/components/ui/textarea';
import {
    type SmsDebugEvent,
    SmsDebugEventTypes,
    type SmsDebugToolCallCompleted,
    type SmsDebugToolCallStarted,
    useSmsDebugEvents,
} from '@apps/web/hooks/use_sms_debug_events';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import { useDebouncedValue } from '@apps/web/lib/use_debounced_value';
import { cn } from '@apps/web/lib/utils';
import {
    useAdminCaregiver,
    useAdminCaregivers,
    useAdminOrganization,
    useAdminOrganizations,
    useAdminSmsDebugContacts,
    useAdminSmsDebugConversations,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';
import {
    type ApiResponseType,
    type Message,
} from '@libraries/typescript/api_client';
import { generateUuid } from '@libraries/typescript/utilities';

type SmsDebugCloneResponse = ApiResponseType<'/admin/sms-debug/clone', 'post'>;
type SmsDebugRunResponse = ApiResponseType<'/admin/sms-debug/run', 'post'>;
type SmsDebugResolveLinksResponse = ApiResponseType<
    '/admin/sms-debug/domain-links/resolve',
    'post'
>;
type SmsDebugContactsResponse = ApiResponseType<
    '/admin/sms-debug/contacts',
    'get'
>;
type SmsDebugConversationsResponse = ApiResponseType<
    '/admin/sms-debug/conversations',
    'get'
>;
type SmsDebugItemRead = SmsDebugRunResponse['items'][number];
type SmsDebugDomainHints = SmsDebugRunResponse['domain_hints'];

type MessageRole = 'user' | 'assistant';

type StreamingThoughtStep = {
    readonly type: 'thought';
    readonly itemId: string;
    readonly content: string;
    readonly summary: string | null;
};

type StreamingToolCallStep = {
    readonly type: 'tool_call';
    readonly id: string;
    readonly name: string;
    readonly arguments: Record<string, unknown>;
    readonly status: 'pending' | 'completed' | 'error';
    readonly result?: unknown;
    readonly error?: string;
};

type StreamingStep = StreamingThoughtStep | StreamingToolCallStep;

type DebugMessage = {
    readonly id: string;
    readonly role: MessageRole;
    readonly body: string;
    readonly timestamp: string;
    readonly domainLinksJson: string;
    readonly streamingSteps?: ReadonlyArray<StreamingStep>;
};

type CaregiverDraft = {
    readonly id: string;
    readonly firstName: string;
    readonly lastName: string;
    readonly preferredName: string;
    readonly language: string;
};

type DomainLinkLike = {
    readonly type?: string;
    readonly callout_id?: string;
    readonly contact_attempt_id?: string;
    readonly shift_confirmation_id?: string;
    readonly agency_name?: string;
    readonly caregiver_first_name?: string;
    readonly shift_date?: string;
    readonly shift_start_time?: string;
    readonly shift_end_time?: string;
    readonly shift_location?: string;
    readonly shift_city?: string;
    readonly patient_name?: string;
    readonly patient_address?: string;
};

type AdminOrganizationOption = ApiResponseType<
    '/admin/organizations',
    'get'
>['organizations'][number];

type AdminCaregiverOption = ApiResponseType<
    '/admin/caregivers',
    'get'
>['caregivers'][number];

type SmsDebugContactOption = SmsDebugContactsResponse['contacts'][number];

type SmsDebugConversationOption =
    SmsDebugConversationsResponse['conversations'][number];

type SmsDebugStreamResponse = ApiResponseType<
    '/admin/sms-debug/run/stream',
    'post'
>;

type StreamingState = {
    readonly runId: string;
    readonly steps: Array<StreamingStep>;
    readonly responseText: string;
    readonly error: string | null;
    readonly isComplete: boolean;
};

const SMS_DEBUG_STATE_KEY = 'sms-debugger:state';
const DEFAULT_DOMAIN_LINKS_JSON = '[]';
const STUB_CALLOUT_ID = '00000000-0000-0000-0000-00000000c001';
const STUB_CONTACT_ATTEMPT_ID = '00000000-0000-0000-0000-00000000c002';
const STUB_SHIFT_CONFIRMATION_ID = '00000000-0000-0000-0000-00000000c003';
const STUB_SHIFT_ID = '00000000-0000-0000-0000-00000000c004';
const STUB_CAREGIVER_ID = '00000000-0000-0000-0000-00000000c005';
const STUB_SHIFT_CONFIRMATION_MULTI_ID_1 =
    '00000000-0000-0000-0000-00000000c006';
const STUB_SHIFT_CONFIRMATION_MULTI_ID_2 =
    '00000000-0000-0000-0000-00000000c007';
const STUB_SHIFT_MULTI_ID_1 = '00000000-0000-0000-0000-00000000c008';
const STUB_SHIFT_MULTI_ID_2 = '00000000-0000-0000-0000-00000000c009';
const STUB_BATCH_SHIFT_ID_1 = '00000000-0000-0000-0000-00000000c010';
const STUB_BATCH_SHIFT_ID_2 = '00000000-0000-0000-0000-00000000c011';
const STUB_BATCH_SHIFT_ID_3 = '00000000-0000-0000-0000-00000000c012';
const DEFAULT_SHIFT_CONFIRMATION_TEMPLATE =
    'Good morning, this is {organization_name}, could you please confirm your upcoming shift for {shift_date} with {patient_name} at {patient_address}';
const DEFAULT_MULTI_SHIFT_CONFIRMATION_TEMPLATE =
    'Good morning, this is {organization_name}, could you please confirm your upcoming shifts:';
const DEFAULT_MULTI_SHIFT_BULLET_TEMPLATE =
    ' - {shift_date} with {patient_name} at {patient_address}';
const DEFAULT_CALLOUT_TEMPLATE =
    'Hey {caregiver_first_name}, we have an open shift {shift_date} from {shift_start_time} to {shift_end_time}{location_text} {caregiver_distance}. {previous_visits}\nHere is a summary of the care plan:\n{care_plan_summary}\nAre you available to pick it up?';
type ScenarioOptionValue =
    | 'callout_stub'
    | 'shift_confirmation_stub'
    | 'multi_shift_confirmation_stub'
    | 'batch_shift_confirmation_stub'
    | 'confirmation_then_callout_stub';

const SCENARIO_OPTIONS: Array<{ value: ScenarioOptionValue; label: string }> = [
    { value: 'callout_stub', label: 'Open Shift active (stub)' },
    {
        value: 'shift_confirmation_stub',
        label: 'Shift confirmation active (stub)',
    },
    {
        value: 'multi_shift_confirmation_stub',
        label: 'Multi-shift confirmation (stub)',
    },
    {
        value: 'batch_shift_confirmation_stub',
        label: 'Batch shift confirmation (stub)',
    },
    {
        value: 'confirmation_then_callout_stub',
        label: 'Confirmation, then open shift (stub)',
    },
];
const SCENARIO_SELECT_OPTIONS: Array<{
    value: ScenarioOptionValue | 'none';
    label: string;
}> = [{ value: 'none', label: 'None' }, ...SCENARIO_OPTIONS];

const DEFAULT_CAREGIVER: CaregiverDraft = {
    id: generateUuid(),
    firstName: 'Test',
    lastName: 'Caregiver',
    preferredName: 'Test',
    language: '',
};

type TimelineStepProps = {
    readonly step: StreamingStep;
};

function TimelineStep({ step }: TimelineStepProps) {
    const [isOpen, setIsOpen] = useState(true);

    if (step.type === 'thought') {
        // Parse summary: first line is title (strip markdown bold **), rest is content
        const summaryLines = step.summary?.split('\n') ?? [];
        const summaryTitle =
            summaryLines[0]?.replace(/^\*\*|\*\*$/g, '') ?? null;
        const summaryContent = summaryLines.slice(1).join('\n').trim();
        const displayContent = step.content || summaryContent;

        return (
            <Collapsible open={isOpen} onOpenChange={setIsOpen}>
                <div className="flex items-start gap-2 py-1">
                    <span className="text-muted-foreground mt-0.5">•</span>
                    <div className="min-w-0 flex-1">
                        <CollapsibleTrigger className="flex items-center gap-1.5 text-left">
                            <span className="text-muted-foreground text-sm">
                                Thought
                            </span>
                            {isOpen ? (
                                <ChevronDown className="text-muted-foreground h-3.5 w-3.5 shrink-0" />
                            ) : (
                                <ChevronRight className="text-muted-foreground h-3.5 w-3.5 shrink-0" />
                            )}
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                            <div className="mt-1 pl-0">
                                {summaryTitle && (
                                    <p className="text-sm font-semibold">
                                        {summaryTitle}
                                    </p>
                                )}
                                {displayContent && (
                                    <Markdown className="text-muted-foreground mt-1 text-sm">
                                        {displayContent}
                                    </Markdown>
                                )}
                            </div>
                        </CollapsibleContent>
                    </div>
                </div>
            </Collapsible>
        );
    }

    return (
        <Collapsible open={isOpen} onOpenChange={setIsOpen}>
            <div className="flex items-start gap-2 py-1">
                <span className="text-muted-foreground mt-0.5">•</span>
                <div className="min-w-0 flex-1">
                    <CollapsibleTrigger className="flex items-center gap-1.5 text-left">
                        {isOpen ? (
                            <ChevronDown className="text-muted-foreground h-3.5 w-3.5 shrink-0" />
                        ) : (
                            <ChevronRight className="text-muted-foreground h-3.5 w-3.5 shrink-0" />
                        )}
                        <code className="text-sm font-medium">{step.name}</code>
                        {step.status === 'pending' && (
                            <Loader2 className="h-3 w-3 animate-spin text-blue-500" />
                        )}
                        {step.status === 'completed' && (
                            <Check className="h-3 w-3 text-green-500" />
                        )}
                        {step.status === 'error' && (
                            <XCircle className="h-3 w-3 text-red-500" />
                        )}
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                        <div className="mt-1 space-y-1 pl-5">
                            <div>
                                <span className="text-muted-foreground text-xs">
                                    Arguments:
                                </span>
                                <pre className="bg-muted mt-0.5 overflow-x-auto rounded p-2 text-xs">
                                    {JSON.stringify(step.arguments, null, 2)}
                                </pre>
                            </div>
                            {step.result !== undefined && (
                                <div>
                                    <span className="text-muted-foreground text-xs">
                                        Result:
                                    </span>
                                    <pre className="bg-muted mt-0.5 overflow-x-auto rounded p-2 text-xs">
                                        {JSON.stringify(step.result, null, 2)}
                                    </pre>
                                </div>
                            )}
                            {step.error && (
                                <div className="text-xs text-red-600 dark:text-red-400">
                                    Error: {step.error}
                                </div>
                            )}
                        </div>
                    </CollapsibleContent>
                </div>
            </div>
        </Collapsible>
    );
}

function getToolResultSummary(step: StreamingToolCallStep): string {
    const name = step.name;
    if (
        name.includes('shift') ||
        name.includes('confirmation') ||
        name.includes('update')
    ) {
        return 'Updated shift confirmation';
    }
    if (
        name.includes('send') ||
        name.includes('sms') ||
        name.includes('text')
    ) {
        return 'Sent SMS response';
    }
    if (name.includes('callout')) {
        return 'Processed outreach';
    }
    return `Completed ${name.replace(/_/g, ' ')}`;
}

type ToolResultSummaryProps = {
    readonly steps: Array<StreamingStep>;
};

function ToolResultSummary({ steps }: ToolResultSummaryProps) {
    const lastToolCall = [...steps]
        .reverse()
        .find((s): s is StreamingToolCallStep => s.type === 'tool_call');

    if (!lastToolCall || lastToolCall.status !== 'completed') {
        return null;
    }

    const summary = getToolResultSummary(lastToolCall);

    return (
        <div className="text-muted-foreground flex items-center gap-1.5 text-sm">
            <Pencil className="h-3.5 w-3.5" />
            <span>{summary}</span>
        </div>
    );
}

type StreamingTimelineProps = {
    readonly steps: Array<StreamingStep>;
    readonly isComplete: boolean;
    readonly onOpenChange?: (isOpen: boolean) => void;
};

function StreamingTimeline({
    steps,
    isComplete,
    onOpenChange,
}: StreamingTimelineProps) {
    const [isOpen, setIsOpen] = useState(true);
    const prevIsComplete = useRef(isComplete);
    const stepCount = steps.length;

    useEffect(() => {
        if (!prevIsComplete.current && isComplete) {
            setIsOpen(false);
            onOpenChange?.(false);
        }
        prevIsComplete.current = isComplete;
    }, [isComplete, onOpenChange]);

    const handleOpenChange = useCallback(
        (open: boolean) => {
            setIsOpen(open);
            onOpenChange?.(open);
        },
        [onOpenChange],
    );

    if (stepCount === 0) {
        return null;
    }

    return (
        <Collapsible open={isOpen} onOpenChange={handleOpenChange}>
            <CollapsibleTrigger className="flex items-center gap-2 py-1">
                {isOpen ? (
                    <ChevronDown className="text-muted-foreground h-4 w-4" />
                ) : (
                    <ChevronRight className="text-muted-foreground h-4 w-4" />
                )}
                <span className="text-muted-foreground text-sm">
                    {stepCount} {stepCount === 1 ? 'step' : 'steps'}
                </span>
                {!isComplete && (
                    <Loader2 className="text-muted-foreground h-3 w-3 animate-spin" />
                )}
            </CollapsibleTrigger>
            <CollapsibleContent>
                <div className="border-muted ml-2 border-l pl-2">
                    {steps.map((step, index) => (
                        <TimelineStep
                            key={
                                step.type === 'tool_call'
                                    ? step.id
                                    : `thought-${index}`
                            }
                            step={step}
                        />
                    ))}
                </div>
            </CollapsibleContent>
        </Collapsible>
    );
}

type StreamingMessageProps = {
    readonly streamingState: StreamingState;
    readonly onCancel: () => void;
};

function StreamingMessage({ streamingState, onCancel }: StreamingMessageProps) {
    const [isTimelineCollapsed, setIsTimelineCollapsed] = useState(false);

    const handleTimelineOpenChange = useCallback((isOpen: boolean) => {
        setIsTimelineCollapsed(!isOpen);
    }, []);

    const showToolResultSummary =
        isTimelineCollapsed &&
        streamingState.isComplete &&
        streamingState.steps.length > 0;

    return (
        <div className="flex justify-start">
            <div className="bg-muted max-w-[85%] space-y-3 rounded-2xl p-3">
                {/* Timeline section */}
                <StreamingTimeline
                    steps={streamingState.steps}
                    isComplete={streamingState.isComplete}
                    onOpenChange={handleTimelineOpenChange}
                />
                {/* Tool result summary (shown when timeline is collapsed) */}
                {showToolResultSummary && (
                    <ToolResultSummary steps={streamingState.steps} />
                )}
                {/* Response text section */}
                <div>
                    <div className="mb-1 flex items-center gap-2">
                        <span className="text-muted-foreground text-xs">
                            {streamingState.responseText
                                ? 'Response'
                                : 'Thinking...'}
                        </span>
                        {!streamingState.isComplete && (
                            <Loader2 className="text-muted-foreground h-3 w-3 animate-spin" />
                        )}
                    </div>
                    {streamingState.responseText ? (
                        <p className="text-sm whitespace-pre-wrap">
                            {streamingState.responseText}
                        </p>
                    ) : (
                        <p className="text-muted-foreground text-sm italic">
                            Generating response…
                        </p>
                    )}
                </div>
                {/* Error display */}
                {streamingState.error && (
                    <div className="rounded-lg border border-red-300 bg-red-50 p-2 text-red-700 dark:border-red-900 dark:bg-red-950/50 dark:text-red-300">
                        <p className="text-sm font-medium">Error</p>
                        <p className="text-sm">{streamingState.error}</p>
                    </div>
                )}
                {/* Cancel button */}
                {!streamingState.isComplete && (
                    <div>
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={onCancel}
                            className="h-7 rounded-full px-3 text-xs"
                        >
                            Cancel
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
}

export const Route = createFileRoute('/_app/admin/sms-debug')({
    head: () => ({
        meta: [
            {
                title: 'SMS Debugger | Phoebe Admin',
            },
        ],
    }),
    component: SmsDebugPage,
});

function SmsDebugPage() {
    const { data: organizationsResponse } = useAdminOrganizations({
        page: 1,
        per_page: 200,
    });

    const [organizationId, setOrganizationId] = useState('');
    const [conversationId, setConversationId] = useState(generateUuid());
    const [contactId, setContactId] = useState(generateUuid());
    const [caregiver, setCaregiver] =
        useState<CaregiverDraft>(DEFAULT_CAREGIVER);
    const [filterContactsByCaregiver, setFilterContactsByCaregiver] =
        useState(false);
    const [messages, setMessages] = useState<Array<DebugMessage>>([]);
    const [editingMessageId, setEditingMessageId] = useState<string | null>(
        null,
    );
    const [editingBody, setEditingBody] = useState('');
    const [inboundBody, setInboundBody] = useState('');
    const [modelOverride, setModelOverride] = useState('');
    const [cloneConversationId, setCloneConversationId] = useState('');
    const [cloneLimit, setCloneLimit] = useState(50);
    const [domainLinkErrors, setDomainLinkErrors] = useState<
        Record<string, string>
    >({});
    const [runResult, setRunResult] = useState<SmsDebugRunResponse | null>(
        null,
    );
    const [runDetailsByMessageId, setRunDetailsByMessageId] = useState<
        Record<string, SmsDebugRunResponse>
    >({});
    const [isCloning, setIsCloning] = useState(false);
    const [isRunning, setIsRunning] = useState(false);
    const [selectedMessageId, setSelectedMessageId] = useState<string | null>(
        null,
    );
    const [traceModalMessageId, setTraceModalMessageId] = useState<
        string | null
    >(null);
    const [domainLinkModalState, setDomainLinkModalState] = useState<{
        messageId: string;
        linkIndex: number;
    } | null>(null);
    const [lastRunMessageId, setLastRunMessageId] = useState<string | null>(
        null,
    );
    const [scenarioContactAttemptId, setScenarioContactAttemptId] =
        useState('');
    const [scenarioShiftConfirmationId, setScenarioShiftConfirmationId] =
        useState('');
    const [isResolvingDomainLinks, setIsResolvingDomainLinks] = useState(false);
    const [streamingState, setStreamingState] = useState<StreamingState | null>(
        null,
    );

    const isStreaming = streamingState !== null && !streamingState.isComplete;

    const { data: selectedOrganizationResponse } = useAdminOrganization(
        organizationId || null,
    );

    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const scrollAreaRef = useRef<HTMLDivElement>(null);

    const scrollToBottom = useCallback(() => {
        const viewport = scrollAreaRef.current?.querySelector(
            '[data-slot="scroll-area-viewport"]',
        );
        if (viewport) {
            viewport.scrollTop = viewport.scrollHeight;
        }
    }, []);

    const organizations = useMemo(
        () => organizationsResponse?.organizations ?? [],
        [organizationsResponse],
    );

    const selectedOrganization = useMemo(
        () =>
            selectedOrganizationResponse ??
            organizations.find((org) => org.id === organizationId) ??
            null,
        [organizations, organizationId, selectedOrganizationResponse],
    );

    const organizationTimezone = selectedOrganization?.timezone;

    // Commented out with Conversation History section
    // const selectedMessage = useMemo(
    //     () =>
    //         messages.find((message) => message.id === selectedMessageId) ??
    //         null,
    //     [messages, selectedMessageId],
    // );

    const traceModalMessage = useMemo(
        () =>
            messages.find((message) => message.id === traceModalMessageId) ??
            null,
        [messages, traceModalMessageId],
    );

    const traceModalRunResult = useMemo(
        () =>
            traceModalMessageId
                ? (runDetailsByMessageId[traceModalMessageId] ?? null)
                : null,
        [runDetailsByMessageId, traceModalMessageId],
    );

    const domainLinkModalMessage = useMemo(
        () =>
            domainLinkModalState
                ? (messages.find(
                      (message) =>
                          message.id === domainLinkModalState.messageId,
                  ) ?? null)
                : null,
        [domainLinkModalState, messages],
    );

    const domainLinkModalLinks = useMemo(
        () =>
            domainLinkModalMessage
                ? parseDomainLinksForDisplay(
                      domainLinkModalMessage.domainLinksJson,
                  )
                : [],
        [domainLinkModalMessage],
    );

    const latestAssistantMessage = useMemo(
        () =>
            [...messages]
                .reverse()
                .find((message) => message.role === 'assistant') ?? null,
        [messages],
    );

    const latestAssistantLinks = useMemo<Array<DomainLinkLike> | null>(() => {
        if (!latestAssistantMessage) {
            return [];
        }
        if (!latestAssistantMessage.domainLinksJson.trim()) {
            return [];
        }
        try {
            const parsed = JSON.parse(
                latestAssistantMessage.domainLinksJson,
            ) as unknown;
            if (!Array.isArray(parsed)) {
                return null;
            }
            return parsed as Array<DomainLinkLike>;
        } catch (error) {
            return null;
        }
    }, [latestAssistantMessage]);

    const latestAssistantLinkError = useMemo(() => {
        if (!latestAssistantMessage) {
            return null;
        }
        return domainLinkErrors[latestAssistantMessage.id] ?? null;
    }, [domainLinkErrors, latestAssistantMessage]);

    const latestAssistantLinksValid = latestAssistantLinks !== null;
    const [selectedScenario, setSelectedScenario] = useState<
        ScenarioOptionValue | 'none'
    >('none');
    const [scenarioSelectOpen, setScenarioSelectOpen] = useState(false);
    const selectedScenarioLabel = useMemo(
        () =>
            SCENARIO_SELECT_OPTIONS.find(
                (option) => option.value === selectedScenario,
            )?.label ?? 'None',
        [selectedScenario],
    );

    const displayMessages = useMemo<Array<Message>>(
        () =>
            messages.map((message) => ({
                id: message.id,
                conversation_id: conversationId,
                // Swap roles so Phoebe (assistant) renders on the left in debug UI.
                role: message.role === 'assistant' ? 'user' : 'assistant',
                content: message.body ?? '',
                created_at: message.timestamp,
                sent_status: 'sent',
            })),
        [messages, conversationId],
    );

    const domainLinksByMessageId = useMemo(() => {
        const map = new Map<string, string>();
        for (const message of messages) {
            map.set(message.id, message.domainLinksJson);
        }
        return map;
    }, [messages]);

    const streamingStepsByMessageId = useMemo(() => {
        const map = new Map<string, ReadonlyArray<StreamingStep>>();
        for (const message of messages) {
            if (message.streamingSteps) {
                map.set(message.id, message.streamingSteps);
            }
        }
        return map;
    }, [messages]);

    useEffect(() => {
        if (organizationId || organizations.length === 0) {
            return;
        }
        const firstOrgId = organizations[0]?.id;
        if (firstOrgId) {
            setOrganizationId(firstOrgId);
        }
    }, [organizationId, organizations]);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }
        const raw = window.localStorage.getItem(SMS_DEBUG_STATE_KEY);
        if (!raw) {
            return;
        }
        try {
            const parsed = JSON.parse(raw) as Partial<{
                organizationId: string;
                conversationId: string;
                contactId: string;
                caregiver: CaregiverDraft;
                filterContactsByCaregiver: boolean;
                messages: Array<DebugMessage>;
                inboundBody: string;
                modelOverride: string;
                cloneConversationId: string;
                cloneLimit: number;
                scenarioContactAttemptId: string;
                scenarioShiftConfirmationId: string;
            }>;
            if (parsed.organizationId) {
                setOrganizationId(parsed.organizationId);
            }
            if (parsed.conversationId) {
                setConversationId(parsed.conversationId);
            }
            if (parsed.contactId) {
                setContactId(parsed.contactId);
            }
            if (parsed.caregiver) {
                setCaregiver(parsed.caregiver);
            }
            if (parsed.filterContactsByCaregiver !== undefined) {
                setFilterContactsByCaregiver(parsed.filterContactsByCaregiver);
            }
            if (parsed.messages) {
                setMessages(parsed.messages);
            }
            if (parsed.inboundBody) {
                setInboundBody(parsed.inboundBody);
            }
            if (parsed.modelOverride) {
                setModelOverride(parsed.modelOverride);
            }
            if (parsed.cloneConversationId) {
                setCloneConversationId(parsed.cloneConversationId);
            }
            if (parsed.cloneLimit) {
                setCloneLimit(parsed.cloneLimit);
            }
            if (parsed.scenarioContactAttemptId) {
                setScenarioContactAttemptId(parsed.scenarioContactAttemptId);
            }
            if (parsed.scenarioShiftConfirmationId) {
                setScenarioShiftConfirmationId(
                    parsed.scenarioShiftConfirmationId,
                );
            }
        } catch (error) {
            console.error('Failed to restore SMS debug state', error);
        }
    }, []);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }
        window.localStorage.setItem(
            SMS_DEBUG_STATE_KEY,
            JSON.stringify({
                organizationId,
                conversationId,
                contactId,
                caregiver,
                filterContactsByCaregiver,
                messages,
                inboundBody,
                modelOverride,
                cloneConversationId,
                cloneLimit,
                scenarioContactAttemptId,
                scenarioShiftConfirmationId,
            }),
        );
    }, [
        organizationId,
        conversationId,
        contactId,
        caregiver,
        filterContactsByCaregiver,
        messages,
        inboundBody,
        modelOverride,
        cloneConversationId,
        cloneLimit,
        scenarioContactAttemptId,
        scenarioShiftConfirmationId,
    ]);

    const handleAddMessage = useCallback((role: MessageRole = 'assistant') => {
        const nextMessage: DebugMessage = {
            id: generateUuid(),
            role,
            body: '',
            timestamp: new Date().toISOString(),
            domainLinksJson: DEFAULT_DOMAIN_LINKS_JSON,
        };
        setMessages((current) => [...current, nextMessage]);
        setSelectedMessageId(nextMessage.id);
        // Start editing the new message immediately
        setEditingMessageId(nextMessage.id);
        setEditingBody('');
    }, []);

    const handleRemoveMessage = useCallback((messageId: string) => {
        setMessages((current) =>
            current.filter((message) => message.id !== messageId),
        );
        setDomainLinkErrors((current) => {
            const next = { ...current };
            delete next[messageId];
            return next;
        });
        setRunDetailsByMessageId((current) => {
            if (!current[messageId]) {
                return current;
            }
            const next = { ...current };
            delete next[messageId];
            return next;
        });
        setSelectedMessageId((current) =>
            current === messageId ? null : current,
        );
        setLastRunMessageId((current) =>
            current === messageId ? null : current,
        );
        setTraceModalMessageId((current) =>
            current === messageId ? null : current,
        );
        setDomainLinkModalState((current) =>
            current?.messageId === messageId ? null : current,
        );
    }, []);

    const handleUpdateMessage = useCallback(
        (messageId: string, updates: Partial<DebugMessage>) => {
            setMessages((current) =>
                current.map((message) =>
                    message.id === messageId
                        ? { ...message, ...updates }
                        : message,
                ),
            );
        },
        [],
    );

    const startEditingMessage = useCallback(
        (messageId: string) => {
            const message = messages.find((m) => m.id === messageId);
            if (message) {
                setEditingMessageId(messageId);
                setEditingBody(message.body);
            }
        },
        [messages],
    );

    const saveEditingMessage = useCallback(() => {
        if (editingMessageId) {
            handleUpdateMessage(editingMessageId, { body: editingBody });
            setEditingMessageId(null);
            setEditingBody('');
        }
    }, [editingMessageId, editingBody, handleUpdateMessage]);

    const cancelEditingMessage = useCallback(() => {
        setEditingMessageId(null);
        setEditingBody('');
    }, []);

    const handleCloneConversation = useCallback(async () => {
        if (!cloneConversationId.trim()) {
            toast.error('Enter a conversation ID to clone', {
                persistNotification: false,
            });
            return;
        }
        setIsCloning(true);
        try {
            const response = (await getApiClient().post({
                url: '/admin/sms-debug/clone',
                body: {
                    conversation_id: cloneConversationId.trim(),
                    limit: cloneLimit,
                },
            })) as SmsDebugCloneResponse;

            const clonedMessages = response.messages.map((message) => ({
                id: message.id,
                role: message.role as MessageRole,
                body: message.body ?? '',
                timestamp: message.timestamp,
                domainLinksJson: JSON.stringify(
                    message.domain_links ?? [],
                    null,
                    2,
                ),
            }));

            setOrganizationId(response.organization_id);
            setConversationId(response.conversation_id);
            setContactId(response.contact_id);
            setCaregiver({
                id: response.caregiver.id,
                firstName: response.caregiver.first_name,
                lastName: response.caregiver.last_name,
                preferredName: response.caregiver.preferred_name ?? '',
                language: response.caregiver.language ?? '',
            });
            setMessages(clonedMessages);
            setSelectedMessageId(
                clonedMessages[clonedMessages.length - 1]?.id ?? null,
            );
            setDomainLinkErrors({});
            setRunResult(null);
            setRunDetailsByMessageId({});
            setLastRunMessageId(null);
            setTraceModalMessageId(null);
            toast.success('Conversation cloned', {
                persistNotification: false,
            });
        } catch (error) {
            toast.error('Failed to clone conversation', {
                persistNotification: false,
            });
        } finally {
            setIsCloning(false);
        }
    }, [cloneConversationId, cloneLimit]);

    const parseDomainLinks = useCallback((message: DebugMessage) => {
        const clearError = () => {
            setDomainLinkErrors((current) => {
                if (!current[message.id]) {
                    return current;
                }
                const next = { ...current };
                delete next[message.id];
                return next;
            });
        };

        if (!message.domainLinksJson.trim()) {
            clearError();
            return [];
        }

        try {
            const parsed = JSON.parse(message.domainLinksJson) as unknown;
            if (!Array.isArray(parsed)) {
                throw new Error('Domain links must be a JSON array');
            }
            clearError();
            return parsed;
        } catch (error) {
            setDomainLinkErrors((current) => ({
                ...current,
                [message.id]:
                    error instanceof Error ? error.message : 'Invalid JSON',
            }));
            return null;
        }
    }, []);

    const createAssistantMessageWithLinks = useCallback(
        (links: Array<DomainLinkLike>, options?: { replace?: boolean }) => {
            const body = buildDefaultMessageForLinks(links);
            const newMessage: DebugMessage = {
                id: generateUuid(),
                role: 'assistant',
                body,
                timestamp: new Date().toISOString(),
                domainLinksJson: JSON.stringify(links, null, 2),
            };
            setMessages((current) =>
                options?.replace ? [newMessage] : [...current, newMessage],
            );
            setSelectedMessageId(newMessage.id);
            setDomainLinkErrors((current) => {
                if (!current[newMessage.id]) {
                    return current;
                }
                const next = { ...current };
                delete next[newMessage.id];
                return next;
            });
            return newMessage;
        },
        [],
    );

    const appendLinksToLatestAssistant = useCallback(
        (linksToAdd: Array<DomainLinkLike>) => {
            if (!latestAssistantMessage) {
                createAssistantMessageWithLinks(linksToAdd);
                toast.success(
                    'Added an assistant message to attach scenario links',
                    { persistNotification: false },
                );
                return;
            }

            const currentLinks = parseDomainLinks(latestAssistantMessage);
            if (currentLinks === null) {
                createAssistantMessageWithLinks(linksToAdd);
                toast.success(
                    'Added an assistant message to attach scenario links',
                    { persistNotification: false },
                );
                return;
            }

            const typedLinks = currentLinks as Array<DomainLinkLike>;
            const nextLinks = [...typedLinks];
            let addedAny = false;

            for (const link of linksToAdd) {
                const hasDuplicate = nextLinks.some((existing) => {
                    if (existing.type !== link.type) {
                        return false;
                    }
                    if (link.type === 'callout_contact_attempt') {
                        return (
                            existing.contact_attempt_id ===
                                link.contact_attempt_id ||
                            existing.callout_id === link.callout_id
                        );
                    }
                    if (link.type === 'shift_confirmation') {
                        return (
                            existing.shift_confirmation_id ===
                            link.shift_confirmation_id
                        );
                    }
                    return false;
                });
                if (!hasDuplicate) {
                    nextLinks.push(link);
                    addedAny = true;
                }
            }

            if (!addedAny) {
                toast('Domain link already attached', {
                    persistNotification: false,
                });
                return;
            }

            handleUpdateMessage(latestAssistantMessage.id, {
                domainLinksJson: JSON.stringify(nextLinks, null, 2),
            });
            setSelectedMessageId(latestAssistantMessage.id);
        },
        [
            createAssistantMessageWithLinks,
            handleUpdateMessage,
            latestAssistantMessage,
            parseDomainLinks,
        ],
    );

    const buildStubCalloutLink = useCallback(
        () => ({
            type: 'callout_contact_attempt',
            callout_id: STUB_CALLOUT_ID,
            contact_attempt_id: STUB_CONTACT_ATTEMPT_ID,
            caregiver_id: caregiver.id || STUB_CAREGIVER_ID,
            shift_id: STUB_SHIFT_ID,
            agency_name: selectedOrganization?.name ?? 'Test Agency',
            caregiver_first_name:
                caregiver.preferredName || caregiver.firstName || 'Caregiver',
            shift_date: 'Jan 15, 2026',
            shift_start_time: '9:00 AM',
            shift_end_time: '5:00 PM',
            shift_location: '123 Main St',
            shift_city: 'Austin',
            shift_state: 'TX',
            role_required: 'RN',
            instructions: 'Call before arrival',
            patient_name: 'John Smith',
        }),
        [caregiver, selectedOrganization?.name],
    );

    const buildStubShiftConfirmationLink = useCallback(
        (
            overrides: {
                shiftConfirmationId?: string;
                shiftId?: string;
                patientName?: string;
                shiftDate?: string;
                shiftStartTime?: string;
                shiftEndTime?: string;
                shiftLocation?: string;
                shiftCity?: string;
                shiftState?: string;
                roleRequired?: string;
            } = {},
        ) => ({
            type: 'shift_confirmation',
            shift_confirmation_id:
                overrides.shiftConfirmationId ?? STUB_SHIFT_CONFIRMATION_ID,
            caregiver_id: caregiver.id || STUB_CAREGIVER_ID,
            shift_id: overrides.shiftId ?? STUB_SHIFT_ID,
            agency_name: selectedOrganization?.name ?? 'Test Agency',
            patient_name: overrides.patientName ?? 'John Smith',
            shift_date: overrides.shiftDate ?? 'Jan 15, 2026',
            shift_start_time: overrides.shiftStartTime ?? '9:00 AM',
            shift_end_time: overrides.shiftEndTime ?? '5:00 PM',
            shift_location: overrides.shiftLocation ?? '123 Main St',
            shift_city: overrides.shiftCity ?? 'Austin',
            shift_state: overrides.shiftState ?? 'TX',
            role_required: overrides.roleRequired ?? 'RN',
        }),
        [caregiver, selectedOrganization?.name],
    );

    const buildStubMultiShiftConfirmationLinks = useCallback(
        () => [
            buildStubShiftConfirmationLink({
                shiftConfirmationId: STUB_SHIFT_CONFIRMATION_MULTI_ID_1,
                shiftId: STUB_SHIFT_MULTI_ID_1,
                patientName: 'John Smith',
                shiftDate: 'Jan 16, 2026',
                shiftStartTime: '8:00 AM',
                shiftEndTime: '12:00 PM',
                shiftLocation: '123 Main St',
            }),
            buildStubShiftConfirmationLink({
                shiftConfirmationId: STUB_SHIFT_CONFIRMATION_MULTI_ID_2,
                shiftId: STUB_SHIFT_MULTI_ID_2,
                patientName: 'Jane Doe',
                shiftDate: 'Jan 17, 2026',
                shiftStartTime: '1:00 PM',
                shiftEndTime: '5:00 PM',
                shiftLocation: '456 Oak Ave',
            }),
        ],
        [buildStubShiftConfirmationLink],
    );

    const buildStubBatchShiftConfirmationLinks = useCallback(
        () => [
            buildStubShiftConfirmationLink({
                shiftId: STUB_BATCH_SHIFT_ID_1,
                patientName: 'Alice Johnson',
                shiftDate: 'Jan 20, 2026',
                shiftStartTime: '7:00 AM',
                shiftEndTime: '3:00 PM',
                shiftLocation: '100 Pine St',
            }),
            buildStubShiftConfirmationLink({
                shiftId: STUB_BATCH_SHIFT_ID_2,
                patientName: 'Bob Williams',
                shiftDate: 'Jan 20, 2026',
                shiftStartTime: '3:00 PM',
                shiftEndTime: '11:00 PM',
                shiftLocation: '200 Elm Ave',
            }),
            buildStubShiftConfirmationLink({
                shiftId: STUB_BATCH_SHIFT_ID_3,
                patientName: 'Carol Davis',
                shiftDate: 'Jan 21, 2026',
                shiftStartTime: '7:00 AM',
                shiftEndTime: '3:00 PM',
                shiftLocation: '300 Cedar Ln',
            }),
        ],
        [buildStubShiftConfirmationLink],
    );

    const resetScenarioState = useCallback(() => {
        setMessages([]);
        setDomainLinkErrors({});
        setRunResult(null);
        setRunDetailsByMessageId({});
        setSelectedMessageId(null);
        setLastRunMessageId(null);
        setTraceModalMessageId(null);
        setDomainLinkModalState(null);
        setEditingMessageId(null);
        setEditingBody('');
        setInboundBody('');
        setScenarioContactAttemptId('');
        setScenarioShiftConfirmationId('');
    }, []);

    const handleApplyScenario = useCallback(() => {
        resetScenarioState();

        if (selectedScenario === 'callout_stub') {
            createAssistantMessageWithLinks([buildStubCalloutLink()], {
                replace: true,
            });
            return;
        }
        if (selectedScenario === 'shift_confirmation_stub') {
            createAssistantMessageWithLinks(
                [buildStubShiftConfirmationLink()],
                { replace: true },
            );
            return;
        }
        if (selectedScenario === 'multi_shift_confirmation_stub') {
            createAssistantMessageWithLinks(
                buildStubMultiShiftConfirmationLinks(),
                { replace: true },
            );
            return;
        }
        if (selectedScenario === 'batch_shift_confirmation_stub') {
            createAssistantMessageWithLinks(
                buildStubBatchShiftConfirmationLinks(),
                { replace: true },
            );
            return;
        }
        if (selectedScenario === 'confirmation_then_callout_stub') {
            // First message: shift confirmation
            createAssistantMessageWithLinks(
                [buildStubShiftConfirmationLink()],
                { replace: true },
            );
            // Second message: callout
            createAssistantMessageWithLinks([buildStubCalloutLink()]);
        }
    }, [
        buildStubBatchShiftConfirmationLinks,
        buildStubCalloutLink,
        buildStubMultiShiftConfirmationLinks,
        buildStubShiftConfirmationLink,
        createAssistantMessageWithLinks,
        resetScenarioState,
        selectedScenario,
    ]);

    const resolveAndAttachLinks = useCallback(
        async (payload: {
            contact_attempt_id?: string;
            shift_confirmation_id?: string;
        }) => {
            setIsResolvingDomainLinks(true);
            try {
                const response = (await getApiClient().post({
                    url: '/admin/sms-debug/domain-links/resolve',
                    body: payload,
                })) as SmsDebugResolveLinksResponse;

                if (response.links.length === 0) {
                    toast.error('No domain links returned', {
                        persistNotification: false,
                    });
                    return;
                }

                appendLinksToLatestAssistant(
                    response.links as Array<DomainLinkLike>,
                );
                toast.success('Domain link attached', {
                    persistNotification: false,
                });
            } catch (error) {
                toast.error('Failed to resolve domain link', {
                    persistNotification: false,
                });
            } finally {
                setIsResolvingDomainLinks(false);
            }
        },
        [appendLinksToLatestAssistant],
    );

    const handleAttachContactAttempt = useCallback(async () => {
        const trimmed = scenarioContactAttemptId.trim();
        if (!trimmed) {
            toast.error('Enter a contact attempt ID', {
                persistNotification: false,
            });
            return;
        }
        await resolveAndAttachLinks({ contact_attempt_id: trimmed });
    }, [resolveAndAttachLinks, scenarioContactAttemptId]);

    const handleAttachShiftConfirmation = useCallback(async () => {
        const trimmed = scenarioShiftConfirmationId.trim();
        if (!trimmed) {
            toast.error('Enter a shift confirmation ID', {
                persistNotification: false,
            });
            return;
        }
        await resolveAndAttachLinks({ shift_confirmation_id: trimmed });
    }, [resolveAndAttachLinks, scenarioShiftConfirmationId]);

    const handleRun = useCallback(async () => {
        if (isRunning || isStreaming) {
            return;
        }
        if (!organizationId) {
            toast.error('Select an organization', {
                persistNotification: false,
            });
            return;
        }
        if (!conversationId.trim()) {
            toast.error('Conversation ID is required', {
                persistNotification: false,
            });
            return;
        }
        if (!contactId.trim()) {
            toast.error('Contact ID is required', {
                persistNotification: false,
            });
            return;
        }
        if (!inboundBody.trim()) {
            toast.error('Enter a test message', { persistNotification: false });
            return;
        }

        const parsedMessages = [];
        const errors: Record<string, string> = {};

        for (const message of messages) {
            const domainLinks = parseDomainLinks(message);
            if (domainLinks === null) {
                errors[message.id] = 'Invalid domain links JSON';
                continue;
            }
            parsedMessages.push({
                id: message.id,
                role: message.role,
                body: message.body || null,
                timestamp: message.timestamp,
                domain_links: domainLinks,
            });
        }

        if (Object.keys(errors).length > 0) {
            setDomainLinkErrors(errors);
            toast.error('Fix domain link JSON before running', {
                persistNotification: false,
            });
            return;
        }

        // Persist previous run's response to messages before clearing
        if (streamingState?.isComplete && streamingState.responseText) {
            const assistantMessage: DebugMessage = {
                id: generateUuid(),
                role: 'assistant',
                body: streamingState.responseText,
                timestamp: new Date().toISOString(),
                domainLinksJson: DEFAULT_DOMAIN_LINKS_JSON,
                streamingSteps: streamingState.steps,
            };
            setMessages((current) => [...current, assistantMessage]);
        }
        setStreamingState(null);
        setIsRunning(true);
        const inboundMessageBody = inboundBody;
        try {
            // Use streaming endpoint
            const response = (await getApiClient().post({
                url: '/admin/sms-debug/run/stream',
                body: {
                    organization_id: organizationId,
                    conversation_id: conversationId,
                    contact_id: contactId,
                    caregiver: {
                        id: caregiver.id,
                        first_name: caregiver.firstName,
                        last_name: caregiver.lastName,
                        preferred_name:
                            caregiver.preferredName.trim().length > 0
                                ? caregiver.preferredName
                                : null,
                        language:
                            caregiver.language.trim().length > 0
                                ? caregiver.language
                                : null,
                    },
                    messages: parsedMessages,
                    inbound_body: inboundMessageBody,
                    model: modelOverride.trim() || null,
                },
            })) as SmsDebugStreamResponse;

            // Add inbound user message immediately
            const nowIso = new Date().toISOString();
            const inboundMessageId = generateUuid();
            const inboundMessage: DebugMessage = {
                id: inboundMessageId,
                role: 'user',
                body: inboundMessageBody,
                timestamp: nowIso,
                domainLinksJson: DEFAULT_DOMAIN_LINKS_JSON,
            };
            setMessages((current) => [...current, inboundMessage]);

            // Scroll to bottom after DOM updates
            setTimeout(scrollToBottom, 0);

            // Initialize streaming state
            setStreamingState({
                runId: response.run_id,
                steps: [],
                responseText: '',
                error: null,
                isComplete: false,
            });

            // Clear input
            setInboundBody('');
            if (textareaRef.current) {
                textareaRef.current.style.height = 'auto';
            }
        } catch (error) {
            toast.error('Failed to start SMS debug run', {
                persistNotification: false,
            });
        } finally {
            setIsRunning(false);
        }
    }, [
        caregiver,
        contactId,
        conversationId,
        inboundBody,
        isRunning,
        isStreaming,
        messages,
        modelOverride,
        organizationId,
        parseDomainLinks,
        scrollToBottom,
        streamingState?.isComplete,
        streamingState?.responseText,
        streamingState?.steps,
    ]);

    const handleResetConversation = useCallback(() => {
        setConversationId(generateUuid());
        setContactId(generateUuid());
        setCaregiver({
            ...DEFAULT_CAREGIVER,
            id: generateUuid(),
        });
        setMessages([]);
        setInboundBody('');
        setRunResult(null);
        setRunDetailsByMessageId({});
        setDomainLinkErrors({});
        setSelectedMessageId(null);
        setLastRunMessageId(null);
        setScenarioContactAttemptId('');
        setScenarioShiftConfirmationId('');
        setTraceModalMessageId(null);
        setDomainLinkModalState(null);
        setStreamingState(null);
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
        }
        toast.success('Conversation reset', { persistNotification: false });
    }, []);

    const handleStreamingEvent = useCallback((event: SmsDebugEvent) => {
        setStreamingState((current) => {
            if (!current) {
                return current;
            }
            // Ignore events for different runs
            if (event.run_id !== current.runId) {
                return current;
            }

            switch (event.type) {
                case SmsDebugEventTypes.REASONING_CHUNK: {
                    // Check if there's an existing thought step with same itemId - append to it
                    // Otherwise, create a new thought step
                    const existingThoughtIndex = current.steps.findIndex(
                        (s): s is StreamingThoughtStep =>
                            s.type === 'thought' && s.itemId === event.item_id,
                    );
                    if (existingThoughtIndex >= 0) {
                        const existingThought = current.steps[
                            existingThoughtIndex
                        ] as StreamingThoughtStep;
                        const updatedSteps = [...current.steps];
                        updatedSteps[existingThoughtIndex] = {
                            ...existingThought,
                            content: existingThought.content + event.content,
                        };
                        return { ...current, steps: updatedSteps };
                    }
                    // Create new thought step
                    const newThought: StreamingThoughtStep = {
                        type: 'thought',
                        itemId: event.item_id,
                        content: event.content,
                        summary: null,
                    };
                    return {
                        ...current,
                        steps: [...current.steps, newThought],
                    };
                }
                case SmsDebugEventTypes.REASONING_SUMMARY: {
                    // Find the thought step with matching itemId and add summary
                    const existingIndex = current.steps.findIndex(
                        (s): s is StreamingThoughtStep =>
                            s.type === 'thought' && s.itemId === event.item_id,
                    );
                    if (existingIndex >= 0) {
                        const updatedSteps = [...current.steps];
                        updatedSteps[existingIndex] = {
                            ...(updatedSteps[
                                existingIndex
                            ] as StreamingThoughtStep),
                            summary: event.summary,
                        };
                        return { ...current, steps: updatedSteps };
                    }
                    // No existing thought step - create one with just the summary
                    const newThought: StreamingThoughtStep = {
                        type: 'thought',
                        itemId: event.item_id,
                        content: '',
                        summary: event.summary,
                    };
                    return {
                        ...current,
                        steps: [...current.steps, newThought],
                    };
                }
                case SmsDebugEventTypes.TOOL_CALL_STARTED: {
                    const startedEvent = event as SmsDebugToolCallStarted;
                    const newToolCall: StreamingToolCallStep = {
                        type: 'tool_call',
                        id: startedEvent.tool_call_id,
                        name: startedEvent.tool_name,
                        arguments: startedEvent.arguments,
                        status: 'pending',
                    };
                    return {
                        ...current,
                        steps: [...current.steps, newToolCall],
                    };
                }
                case SmsDebugEventTypes.TOOL_CALL_COMPLETED: {
                    const completedEvent = event as SmsDebugToolCallCompleted;
                    return {
                        ...current,
                        steps: current.steps.map((step) =>
                            step.type === 'tool_call' &&
                            step.id === completedEvent.tool_call_id
                                ? {
                                      ...step,
                                      status: completedEvent.error
                                          ? ('error' as const)
                                          : ('completed' as const),
                                      result: completedEvent.result,
                                      error: completedEvent.error ?? undefined,
                                  }
                                : step,
                        ),
                    };
                }
                case SmsDebugEventTypes.RESPONSE_CHUNK:
                    return {
                        ...current,
                        responseText: current.responseText + event.content,
                    };
                case SmsDebugEventTypes.RUN_COMPLETED:
                    return {
                        ...current,
                        isComplete: true,
                    };
                case SmsDebugEventTypes.RUN_ERROR:
                    return {
                        ...current,
                        error: event.error,
                        isComplete: true,
                    };
                case SmsDebugEventTypes.RUN_CANCELLED:
                    return null;
                default:
                    return current;
            }
        });
    }, []);

    // Connect to SMS debug events WebSocket when streaming
    useSmsDebugEvents({
        runId: streamingState?.runId ?? null,
        onEvent: handleStreamingEvent,
        enabled: isStreaming,
    });

    // Handle streaming completion - show toast notification.
    // Keep streamingState visible (with timeline) after completion.
    // The response is persisted to messages when starting a new run.
    useEffect(() => {
        if (!streamingState?.isComplete) {
            return;
        }

        if (streamingState.error) {
            toast.error(`Run failed: ${streamingState.error}`, {
                persistNotification: false,
            });
        } else {
            toast.success('SMS debug run complete', {
                persistNotification: false,
            });
        }
    }, [streamingState?.isComplete, streamingState?.error]);

    const handleCancelStreaming = useCallback(async () => {
        const runId = streamingState?.runId;
        setStreamingState(null);

        if (runId) {
            try {
                await getApiClient().post({
                    url: '/admin/sms-debug/run/{run_id}/cancel',
                    pathParams: { run_id: runId },
                });
            } catch {
                // Ignore errors - the run may have already completed
            }
        }

        toast.info('Run cancelled', { persistNotification: false });
    }, [streamingState?.runId]);

    const snapshotJson = useMemo(() => {
        if (!runResult) {
            return '';
        }
        return JSON.stringify(runResult.snapshot, null, 2);
    }, [runResult]);

    const traceItems = useMemo(() => runResult?.items ?? [], [runResult]);

    const traceModalItems = useMemo(
        () => traceModalRunResult?.items ?? [],
        [traceModalRunResult],
    );

    const traceModalSnapshotJson = useMemo(() => {
        if (!traceModalRunResult) {
            return '';
        }
        return JSON.stringify(traceModalRunResult.snapshot, null, 2);
    }, [traceModalRunResult]);

    const traceModalToolCalls = useMemo(
        () => getToolCallItems(traceModalRunResult?.items ?? []),
        [traceModalRunResult],
    );

    const traceModalReasoningItems = useMemo(
        () => getReasoningItems(traceModalRunResult?.items ?? []),
        [traceModalRunResult],
    );

    const activeDomainHints = useMemo(
        () => runResult?.domain_hints ?? null,
        [runResult],
    );

    return (
        <div className="flex h-full min-h-0 flex-col">
            <div className="border-b px-6 py-4">
                <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
                    <div className="flex items-center gap-3">
                        <Link
                            to="/admin"
                            className="hover:bg-accent rounded-md p-2 transition-colors"
                            aria-label="Back to admin"
                        >
                            <ArrowLeft className="text-muted-foreground h-4 w-4" />
                        </Link>
                        <h1 className="text-2xl font-semibold tracking-tight">
                            SMS Debugger
                        </h1>
                    </div>
                    <div className="text-muted-foreground flex items-center gap-2 text-sm">
                        <Badge variant="outline">Dry run</Badge>
                        No messages are sent or queued
                    </div>
                </div>
            </div>
            <div className="flex min-h-0 flex-1">
                <ResizablePanelGroup
                    orientation="horizontal"
                    className="h-full"
                >
                    <ResizablePanel defaultSize={70} minSize={45}>
                        <div className="flex h-full min-h-0 flex-col">
                            <ScrollArea
                                ref={scrollAreaRef}
                                className="min-h-0 flex-1 px-6 py-4"
                            >
                                <div className="space-y-4 p-1">
                                    {displayMessages.length === 0 ? (
                                        <div className="text-muted-foreground text-sm">
                                            No messages yet. Clone a
                                            conversation or add a message to get
                                            started.
                                        </div>
                                    ) : (
                                        displayMessages.map((message) => {
                                            const isSelected =
                                                message.id ===
                                                selectedMessageId;
                                            const isLastRun =
                                                message.id === lastRunMessageId;
                                            const isEditing =
                                                message.id === editingMessageId;
                                            const highlightLabel =
                                                !isSelected && isLastRun
                                                    ? 'Last run'
                                                    : undefined;
                                            const runDetails =
                                                runDetailsByMessageId[
                                                    message.id
                                                ] ?? null;
                                            const toolCalls = runDetails
                                                ? getToolCallItems(
                                                      runDetails.items,
                                                  )
                                                : [];
                                            const domainLinks =
                                                parseDomainLinksForDisplay(
                                                    domainLinksByMessageId.get(
                                                        message.id,
                                                    ) ?? '',
                                                );
                                            const messageStreamingSteps =
                                                streamingStepsByMessageId.get(
                                                    message.id,
                                                );

                                            // Render messages with streaming steps in same style as StreamingMessage
                                            if (
                                                messageStreamingSteps &&
                                                messageStreamingSteps.length > 0
                                            ) {
                                                return (
                                                    <div
                                                        key={message.id}
                                                        className="space-y-1"
                                                    >
                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                setSelectedMessageId(
                                                                    message.id,
                                                                )
                                                            }
                                                            className="focus-visible:ring-ring flex w-full justify-start border-0 bg-transparent p-0 text-left focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
                                                        >
                                                            <div
                                                                className={cn(
                                                                    'bg-muted max-w-[85%] space-y-3 rounded-2xl p-3',
                                                                    isSelected &&
                                                                        'ring-2 ring-blue-500',
                                                                )}
                                                            >
                                                                <StreamingTimeline
                                                                    steps={[
                                                                        ...messageStreamingSteps,
                                                                    ]}
                                                                    isComplete={
                                                                        true
                                                                    }
                                                                />
                                                                <div>
                                                                    <div className="mb-1">
                                                                        <span className="text-muted-foreground text-xs">
                                                                            Response
                                                                        </span>
                                                                    </div>
                                                                    <p className="text-sm whitespace-pre-wrap">
                                                                        {
                                                                            message.content
                                                                        }
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </button>
                                                        {isSelected ? (
                                                            <div className="flex justify-start pt-1 pl-2">
                                                                <Button
                                                                    type="button"
                                                                    variant="ghost"
                                                                    size="icon"
                                                                    onClick={() =>
                                                                        handleRemoveMessage(
                                                                            message.id,
                                                                        )
                                                                    }
                                                                    className="text-muted-foreground h-7 w-7 hover:text-red-600 dark:hover:text-red-400"
                                                                    aria-label="Delete message"
                                                                >
                                                                    <Trash2 className="h-4 w-4" />
                                                                </Button>
                                                            </div>
                                                        ) : null}
                                                    </div>
                                                );
                                            }

                                            return (
                                                <div
                                                    key={message.id}
                                                    className="space-y-1"
                                                >
                                                    {isEditing ? (
                                                        <div
                                                            className={cn(
                                                                'flex',
                                                                message.role ===
                                                                    'user'
                                                                    ? 'justify-start'
                                                                    : 'justify-end',
                                                            )}
                                                        >
                                                            <div
                                                                className={cn(
                                                                    'max-w-[85%] rounded-2xl px-4 py-2',
                                                                    message.role ===
                                                                        'user'
                                                                        ? 'bg-muted'
                                                                        : 'bg-blue-500 text-white',
                                                                )}
                                                            >
                                                                <textarea
                                                                    autoFocus={
                                                                        true
                                                                    }
                                                                    className={cn(
                                                                        'w-full min-w-[200px] resize-none border-0 bg-transparent p-0 text-sm focus:ring-0 focus:outline-none',
                                                                        message.role ===
                                                                            'assistant' &&
                                                                            'text-white placeholder:text-white/60',
                                                                    )}
                                                                    value={
                                                                        editingBody
                                                                    }
                                                                    placeholder="Type a message..."
                                                                    onChange={(
                                                                        event,
                                                                    ) =>
                                                                        setEditingBody(
                                                                            event
                                                                                .target
                                                                                .value,
                                                                        )
                                                                    }
                                                                    onKeyDown={(
                                                                        event,
                                                                    ) => {
                                                                        if (
                                                                            event.key ===
                                                                                'Enter' &&
                                                                            !event.shiftKey
                                                                        ) {
                                                                            event.preventDefault();
                                                                            saveEditingMessage();
                                                                        } else if (
                                                                            event.key ===
                                                                            'Escape'
                                                                        ) {
                                                                            cancelEditingMessage();
                                                                        }
                                                                    }}
                                                                    rows={2}
                                                                />
                                                                <div
                                                                    className={cn(
                                                                        'mt-1 flex justify-end gap-2 border-t pt-1',
                                                                        message.role ===
                                                                            'user'
                                                                            ? 'border-border'
                                                                            : 'border-white/20',
                                                                    )}
                                                                >
                                                                    <button
                                                                        type="button"
                                                                        onClick={
                                                                            cancelEditingMessage
                                                                        }
                                                                        className={cn(
                                                                            'text-xs opacity-70 hover:opacity-100',
                                                                            message.role ===
                                                                                'user'
                                                                                ? 'text-muted-foreground'
                                                                                : 'text-white',
                                                                        )}
                                                                    >
                                                                        Cancel
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        onClick={
                                                                            saveEditingMessage
                                                                        }
                                                                        className={cn(
                                                                            'text-xs font-medium hover:opacity-80',
                                                                            message.role ===
                                                                                'user'
                                                                                ? 'text-blue-600'
                                                                                : 'text-white',
                                                                        )}
                                                                    >
                                                                        Save
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                setSelectedMessageId(
                                                                    message.id,
                                                                )
                                                            }
                                                            onDoubleClick={() =>
                                                                startEditingMessage(
                                                                    message.id,
                                                                )
                                                            }
                                                            className="focus-visible:ring-ring w-full border-0 bg-transparent p-0 text-left focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
                                                        >
                                                            <MessageItem
                                                                message={
                                                                    message
                                                                }
                                                                timezone={
                                                                    organizationTimezone
                                                                }
                                                                isHighlighted={
                                                                    isSelected ||
                                                                    isLastRun
                                                                }
                                                                highlightLabel={
                                                                    highlightLabel
                                                                }
                                                                showRating={
                                                                    false
                                                                }
                                                            />
                                                        </button>
                                                    )}
                                                    {isSelected &&
                                                    !isEditing ? (
                                                        <div
                                                            className={cn(
                                                                'flex pt-1',
                                                                message.role ===
                                                                    'user'
                                                                    ? 'justify-start pl-2'
                                                                    : 'justify-end pr-2',
                                                            )}
                                                        >
                                                            <Button
                                                                type="button"
                                                                variant="ghost"
                                                                size="icon"
                                                                onClick={() =>
                                                                    handleRemoveMessage(
                                                                        message.id,
                                                                    )
                                                                }
                                                                className="text-muted-foreground h-7 w-7 hover:text-red-600 dark:hover:text-red-400"
                                                                aria-label="Delete message"
                                                            >
                                                                <Trash2 className="h-4 w-4" />
                                                            </Button>
                                                        </div>
                                                    ) : null}
                                                    {domainLinks.length > 0 ? (
                                                        <div
                                                            className={cn(
                                                                'flex flex-wrap gap-2 pt-1',
                                                                message.role ===
                                                                    'user'
                                                                    ? 'justify-start pl-2'
                                                                    : 'justify-end pr-2',
                                                            )}
                                                        >
                                                            {domainLinks.map(
                                                                (
                                                                    link,
                                                                    index,
                                                                ) => {
                                                                    const isActive =
                                                                        isDomainLinkActive(
                                                                            link,
                                                                            activeDomainHints,
                                                                        );
                                                                    return (
                                                                        <Button
                                                                            key={`${getDomainLinkKey(
                                                                                link,
                                                                            )}-${index}`}
                                                                            type="button"
                                                                            variant="outline"
                                                                            size="sm"
                                                                            className="h-7 max-w-[280px] rounded-full px-3 text-xs"
                                                                            title={getDomainLinkSummary(
                                                                                link,
                                                                            )}
                                                                            onClick={() =>
                                                                                setDomainLinkModalState(
                                                                                    {
                                                                                        messageId:
                                                                                            message.id,
                                                                                        linkIndex:
                                                                                            index,
                                                                                    },
                                                                                )
                                                                            }
                                                                        >
                                                                            <span className="truncate">
                                                                                {getDomainLinkSummary(
                                                                                    link,
                                                                                )}
                                                                            </span>
                                                                            {isActive ? (
                                                                                <Badge
                                                                                    variant="outline"
                                                                                    className="ml-2 h-4 px-1.5 text-[10px]"
                                                                                >
                                                                                    Active
                                                                                </Badge>
                                                                            ) : null}
                                                                        </Button>
                                                                    );
                                                                },
                                                            )}
                                                        </div>
                                                    ) : null}
                                                    {toolCalls.length > 0 ? (
                                                        <div
                                                            className={cn(
                                                                'flex flex-wrap gap-2 pt-1',
                                                                message.role ===
                                                                    'user'
                                                                    ? 'justify-start pl-2'
                                                                    : 'justify-end pr-2',
                                                            )}
                                                        >
                                                            {toolCalls.map(
                                                                (
                                                                    item,
                                                                    index,
                                                                ) => (
                                                                    <Button
                                                                        key={`${item.id}-${index}`}
                                                                        type="button"
                                                                        variant="outline"
                                                                        size="sm"
                                                                        className="h-7 max-w-[240px] rounded-full px-3 text-xs"
                                                                        title={
                                                                            item.tool_name ??
                                                                            undefined
                                                                        }
                                                                        onClick={() =>
                                                                            setTraceModalMessageId(
                                                                                message.id,
                                                                            )
                                                                        }
                                                                    >
                                                                        <span className="truncate">
                                                                            {getToolCallLabel(
                                                                                item,
                                                                            )}
                                                                        </span>
                                                                    </Button>
                                                                ),
                                                            )}
                                                        </div>
                                                    ) : null}
                                                </div>
                                            );
                                        })
                                    )}
                                    {/* Streaming message - shown during streaming AND after completion */}
                                    {streamingState && (
                                        <StreamingMessage
                                            streamingState={streamingState}
                                            onCancel={handleCancelStreaming}
                                        />
                                    )}
                                </div>
                            </ScrollArea>
                            <div className="border-t px-6 py-4">
                                <form
                                    className="space-y-3"
                                    onSubmit={(event) => {
                                        event.preventDefault();
                                        void handleRun();
                                    }}
                                >
                                    <div className="flex flex-wrap gap-2">
                                        <Button
                                            type="button"
                                            variant="outline"
                                            className="border-border bg-muted/60 text-muted-foreground hover:bg-muted hover:text-foreground h-7 rounded-full px-3 text-xs font-medium"
                                            onClick={() =>
                                                handleAddMessage('user')
                                            }
                                        >
                                            <span className="h-2 w-2 rounded-full bg-blue-500" />
                                            Add user message
                                        </Button>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            className="border-border bg-muted/60 text-muted-foreground hover:bg-muted hover:text-foreground h-7 rounded-full px-3 text-xs font-medium"
                                            onClick={() =>
                                                handleAddMessage('assistant')
                                            }
                                        >
                                            <span className="h-2 w-2 rounded-full bg-slate-400" />
                                            Add assistant message
                                        </Button>
                                    </div>
                                    <div className="border-border rounded-lg border-1 p-3">
                                        <div className="flex items-center gap-2">
                                            <Textarea
                                                value={inboundBody}
                                                rows={1}
                                                placeholder="Write a message..."
                                                onChange={(event) => {
                                                    setInboundBody(
                                                        event.target.value,
                                                    );
                                                }}
                                                onKeyDown={(event) => {
                                                    if (
                                                        isRunning ||
                                                        isStreaming
                                                    ) {
                                                        return;
                                                    }

                                                    if (
                                                        event.key === 'Enter' &&
                                                        !event.shiftKey
                                                    ) {
                                                        event.preventDefault();
                                                        void handleRun();
                                                    }
                                                }}
                                                disabled={
                                                    isRunning || isStreaming
                                                }
                                                className="max-h-24 min-h-[2.5rem] flex-1 resize-none border-0 bg-transparent p-0 shadow-none focus-visible:ring-0"
                                                style={{
                                                    height: 'auto',
                                                    overflow: 'hidden',
                                                }}
                                                onInput={(event) => {
                                                    const target =
                                                        event.target as HTMLTextAreaElement;
                                                    target.style.height =
                                                        'auto';
                                                    target.style.height =
                                                        Math.min(
                                                            target.scrollHeight,
                                                            96,
                                                        ) + 'px';
                                                }}
                                                ref={textareaRef}
                                            />
                                            <Button
                                                type="submit"
                                                disabled={
                                                    isRunning ||
                                                    isStreaming ||
                                                    !inboundBody.trim()
                                                }
                                                size="icon"
                                                className="h-8 w-8 flex-shrink-0 self-center rounded-full"
                                                aria-label="Run debug turn"
                                            >
                                                {isRunning || isStreaming ? (
                                                    <Loader2 className="h-4 w-4 animate-spin" />
                                                ) : (
                                                    <ArrowUp className="h-4 w-4" />
                                                )}
                                            </Button>
                                        </div>
                                    </div>
                                    <div className="text-muted-foreground flex flex-wrap items-center gap-3 text-xs">
                                        <Label
                                            htmlFor="model-override"
                                            className="text-muted-foreground text-xs"
                                        >
                                            Model override
                                        </Label>
                                        <Input
                                            id="model-override"
                                            placeholder="gpt-5.6-luna"
                                            value={modelOverride}
                                            onChange={(event) =>
                                                setModelOverride(
                                                    event.target.value,
                                                )
                                            }
                                            className="h-8 w-52 text-xs"
                                        />
                                    </div>
                                </form>
                            </div>
                        </div>
                    </ResizablePanel>
                    <ResizableHandle withHandle={true} />
                    <ResizablePanel defaultSize={30} minSize={25}>
                        <ScrollArea className="h-full">
                            <div className="space-y-6 p-6">
                                <Collapsible defaultOpen={true}>
                                    <CollapsibleTrigger className="group flex w-full cursor-pointer items-center justify-between text-left text-sm font-semibold">
                                        <span>Scenario Builder</span>
                                        <ChevronDown className="h-4 w-4 transition-transform group-data-[state=open]:rotate-180" />
                                    </CollapsibleTrigger>
                                    <CollapsibleContent>
                                        <div className="mt-4 space-y-3 rounded-md border p-3">
                                            <div className="space-y-2">
                                                <Label>Load scenario</Label>
                                                <div className="flex items-center gap-2">
                                                    <div className="min-w-0 flex-1">
                                                        <Combobox
                                                            open={
                                                                scenarioSelectOpen
                                                            }
                                                            onOpenChange={
                                                                setScenarioSelectOpen
                                                            }
                                                            displayValue={
                                                                selectedScenarioLabel
                                                            }
                                                            placeholder="Select scenario..."
                                                            hideSearch={true}
                                                        >
                                                            <CommandEmpty>
                                                                No scenarios
                                                                available.
                                                            </CommandEmpty>
                                                            <CommandGroup>
                                                                {SCENARIO_SELECT_OPTIONS.map(
                                                                    (
                                                                        option,
                                                                    ) => {
                                                                        const isSelected =
                                                                            option.value ===
                                                                            selectedScenario;
                                                                        return (
                                                                            <CommandItem
                                                                                key={
                                                                                    option.value
                                                                                }
                                                                                value={`${option.label} ${option.value}`}
                                                                                onSelect={() => {
                                                                                    setSelectedScenario(
                                                                                        option.value,
                                                                                    );
                                                                                    setScenarioSelectOpen(
                                                                                        false,
                                                                                    );
                                                                                }}
                                                                            >
                                                                                <Check
                                                                                    className={cn(
                                                                                        'mr-2 h-4 w-4',
                                                                                        isSelected
                                                                                            ? 'opacity-100'
                                                                                            : 'opacity-0',
                                                                                    )}
                                                                                />
                                                                                {
                                                                                    option.label
                                                                                }
                                                                            </CommandItem>
                                                                        );
                                                                    },
                                                                )}
                                                            </CommandGroup>
                                                        </Combobox>
                                                    </div>
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        onClick={
                                                            handleApplyScenario
                                                        }
                                                    >
                                                        Apply
                                                    </Button>
                                                </div>
                                                {!latestAssistantLinksValid ? (
                                                    <div className="text-destructive text-xs">
                                                        {latestAssistantLinkError ??
                                                            'Fix domain links JSON on the latest assistant message to use scenario selections.'}
                                                    </div>
                                                ) : null}
                                                <div className="text-muted-foreground text-xs">
                                                    Links attach to the latest
                                                    assistant message.
                                                </div>
                                            </div>
                                            <Separator />
                                            <div className="space-y-3">
                                                <div className="space-y-2">
                                                    <Label htmlFor="scenario-contact-attempt">
                                                        Contact attempt ID
                                                    </Label>
                                                    <Input
                                                        id="scenario-contact-attempt"
                                                        placeholder="UUID"
                                                        value={
                                                            scenarioContactAttemptId
                                                        }
                                                        onChange={(event) =>
                                                            setScenarioContactAttemptId(
                                                                event.target
                                                                    .value,
                                                            )
                                                        }
                                                    />
                                                    <Button
                                                        variant="outline"
                                                        className="w-full"
                                                        onClick={
                                                            handleAttachContactAttempt
                                                        }
                                                        disabled={
                                                            isResolvingDomainLinks
                                                        }
                                                    >
                                                        {isResolvingDomainLinks ? (
                                                            <React.Fragment>
                                                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                                Attaching…
                                                            </React.Fragment>
                                                        ) : (
                                                            'Attach outreach'
                                                        )}
                                                    </Button>
                                                </div>
                                                <div className="space-y-2">
                                                    <Label htmlFor="scenario-shift-confirmation">
                                                        Shift confirmation ID
                                                    </Label>
                                                    <Input
                                                        id="scenario-shift-confirmation"
                                                        placeholder="UUID"
                                                        value={
                                                            scenarioShiftConfirmationId
                                                        }
                                                        onChange={(event) =>
                                                            setScenarioShiftConfirmationId(
                                                                event.target
                                                                    .value,
                                                            )
                                                        }
                                                    />
                                                    <Button
                                                        variant="outline"
                                                        className="w-full"
                                                        onClick={
                                                            handleAttachShiftConfirmation
                                                        }
                                                        disabled={
                                                            isResolvingDomainLinks
                                                        }
                                                    >
                                                        {isResolvingDomainLinks ? (
                                                            <React.Fragment>
                                                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                                Attaching…
                                                            </React.Fragment>
                                                        ) : (
                                                            'Attach confirmation'
                                                        )}
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    </CollapsibleContent>
                                </Collapsible>
                                <Separator />
                                <Collapsible defaultOpen={true}>
                                    <CollapsibleTrigger className="group flex w-full cursor-pointer items-center justify-between text-left text-sm font-semibold">
                                        <span>Clone Conversation</span>
                                        <ChevronDown className="h-4 w-4 transition-transform group-data-[state=open]:rotate-180" />
                                    </CollapsibleTrigger>
                                    <CollapsibleContent>
                                        <div className="mt-4 space-y-3">
                                            <div className="space-y-2">
                                                <Label htmlFor="clone-conversation-id">
                                                    Conversation ID
                                                </Label>
                                                <Input
                                                    id="clone-conversation-id"
                                                    placeholder="UUID"
                                                    value={cloneConversationId}
                                                    onChange={(event) =>
                                                        setCloneConversationId(
                                                            event.target.value,
                                                        )
                                                    }
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label htmlFor="clone-limit">
                                                    Message limit
                                                </Label>
                                                <Input
                                                    id="clone-limit"
                                                    type="number"
                                                    min={1}
                                                    max={500}
                                                    value={cloneLimit}
                                                    onChange={(event) =>
                                                        setCloneLimit(
                                                            Number(
                                                                event.target
                                                                    .value,
                                                            ),
                                                        )
                                                    }
                                                />
                                            </div>
                                            <Button
                                                onClick={
                                                    handleCloneConversation
                                                }
                                                disabled={isCloning}
                                                className="w-full"
                                            >
                                                {isCloning ? (
                                                    <React.Fragment>
                                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                        Cloning…
                                                    </React.Fragment>
                                                ) : (
                                                    <React.Fragment>
                                                        <RefreshCw className="mr-2 h-4 w-4" />
                                                        Clone
                                                    </React.Fragment>
                                                )}
                                            </Button>
                                        </div>
                                    </CollapsibleContent>
                                </Collapsible>
                                <Separator />
                                <Collapsible defaultOpen={true}>
                                    <CollapsibleTrigger className="group flex w-full cursor-pointer items-center justify-between text-left text-sm font-semibold">
                                        <span>Debug Metadata</span>
                                        <ChevronDown className="h-4 w-4 transition-transform group-data-[state=open]:rotate-180" />
                                    </CollapsibleTrigger>
                                    <CollapsibleContent>
                                        <div className="mt-4 space-y-3">
                                            <div className="space-y-2">
                                                <Label htmlFor="organization">
                                                    Organization
                                                </Label>
                                                <SmsDebugOrganizationCombobox
                                                    value={organizationId}
                                                    onValueChange={(value) => {
                                                        setOrganizationId(
                                                            value,
                                                        );
                                                    }}
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label htmlFor="conversation-id">
                                                    Conversation ID
                                                </Label>
                                                <SmsDebugConversationCombobox
                                                    value={conversationId}
                                                    organizationId={
                                                        organizationId
                                                    }
                                                    onSelect={(
                                                        record,
                                                        nextValue,
                                                    ) => {
                                                        setConversationId(
                                                            nextValue,
                                                        );
                                                        if (!record) {
                                                            return;
                                                        }
                                                        setOrganizationId(
                                                            record.organization_id,
                                                        );
                                                        setContactId(
                                                            record.contact_id,
                                                        );
                                                        if (
                                                            record.caregiver_id
                                                        ) {
                                                            setCaregiver(
                                                                (current) => ({
                                                                    ...current,
                                                                    id: record.caregiver_id!,
                                                                }),
                                                            );
                                                        }
                                                    }}
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <div className="flex flex-wrap items-center justify-between gap-2">
                                                    <Label htmlFor="contact-id">
                                                        Contact ID
                                                    </Label>
                                                    <div className="flex items-center gap-2 text-xs">
                                                        <Checkbox
                                                            id="filter-contacts-by-caregiver"
                                                            checked={
                                                                filterContactsByCaregiver
                                                            }
                                                            onCheckedChange={(
                                                                checked,
                                                            ) => {
                                                                setFilterContactsByCaregiver(
                                                                    checked ===
                                                                        true,
                                                                );
                                                            }}
                                                        />
                                                        <Label
                                                            htmlFor="filter-contacts-by-caregiver"
                                                            className="text-muted-foreground text-xs"
                                                        >
                                                            Only selected
                                                            caregiver
                                                        </Label>
                                                    </div>
                                                </div>
                                                <SmsDebugContactCombobox
                                                    value={contactId}
                                                    organizationId={
                                                        organizationId
                                                    }
                                                    caregiverId={caregiver.id}
                                                    filterByCaregiver={
                                                        filterContactsByCaregiver
                                                    }
                                                    onSelect={(
                                                        record,
                                                        nextValue,
                                                    ) => {
                                                        setContactId(nextValue);
                                                        if (!record) {
                                                            return;
                                                        }
                                                        setOrganizationId(
                                                            record.organization_id,
                                                        );
                                                        if (
                                                            record.caregiver_id
                                                        ) {
                                                            setCaregiver(
                                                                (current) => ({
                                                                    ...current,
                                                                    id: record.caregiver_id!,
                                                                }),
                                                            );
                                                        }
                                                    }}
                                                />
                                            </div>
                                            <Separator />
                                            <div className="space-y-2">
                                                <Label htmlFor="caregiver-id">
                                                    Caregiver ID
                                                </Label>
                                                <SmsDebugCaregiverCombobox
                                                    value={caregiver.id}
                                                    organizationId={
                                                        organizationId
                                                    }
                                                    onSelect={(
                                                        record,
                                                        nextValue,
                                                    ) => {
                                                        if (!record) {
                                                            setCaregiver(
                                                                (current) => ({
                                                                    ...current,
                                                                    id: nextValue,
                                                                }),
                                                            );
                                                            return;
                                                        }
                                                        const nameParts =
                                                            splitFullName(
                                                                record.name,
                                                            );
                                                        setCaregiver({
                                                            id: record.id,
                                                            firstName:
                                                                record.first_name ??
                                                                nameParts.firstName,
                                                            lastName:
                                                                record.last_name ??
                                                                nameParts.lastName,
                                                            preferredName:
                                                                record.preferred_name ??
                                                                record.first_name ??
                                                                nameParts.firstName,
                                                            language:
                                                                record.primary_language ??
                                                                '',
                                                        });
                                                    }}
                                                />
                                            </div>
                                            <div className="grid gap-3 md:grid-cols-2">
                                                <div className="space-y-2">
                                                    <Label htmlFor="caregiver-first-name">
                                                        First name
                                                    </Label>
                                                    <Input
                                                        id="caregiver-first-name"
                                                        value={
                                                            caregiver.firstName
                                                        }
                                                        onChange={(event) =>
                                                            setCaregiver(
                                                                (current) => ({
                                                                    ...current,
                                                                    firstName:
                                                                        event
                                                                            .target
                                                                            .value,
                                                                }),
                                                            )
                                                        }
                                                    />
                                                </div>
                                                <div className="space-y-2">
                                                    <Label htmlFor="caregiver-last-name">
                                                        Last name
                                                    </Label>
                                                    <Input
                                                        id="caregiver-last-name"
                                                        value={
                                                            caregiver.lastName
                                                        }
                                                        onChange={(event) =>
                                                            setCaregiver(
                                                                (current) => ({
                                                                    ...current,
                                                                    lastName:
                                                                        event
                                                                            .target
                                                                            .value,
                                                                }),
                                                            )
                                                        }
                                                    />
                                                </div>
                                            </div>
                                            <div className="grid gap-3 md:grid-cols-2">
                                                <div className="space-y-2">
                                                    <Label htmlFor="caregiver-preferred-name">
                                                        Preferred name
                                                    </Label>
                                                    <Input
                                                        id="caregiver-preferred-name"
                                                        value={
                                                            caregiver.preferredName
                                                        }
                                                        onChange={(event) =>
                                                            setCaregiver(
                                                                (current) => ({
                                                                    ...current,
                                                                    preferredName:
                                                                        event
                                                                            .target
                                                                            .value,
                                                                }),
                                                            )
                                                        }
                                                    />
                                                </div>
                                                <div className="space-y-2">
                                                    <Label htmlFor="caregiver-language">
                                                        Language
                                                    </Label>
                                                    <Input
                                                        id="caregiver-language"
                                                        placeholder="en, es, ..."
                                                        value={
                                                            caregiver.language
                                                        }
                                                        onChange={(event) =>
                                                            setCaregiver(
                                                                (current) => ({
                                                                    ...current,
                                                                    language:
                                                                        event
                                                                            .target
                                                                            .value,
                                                                }),
                                                            )
                                                        }
                                                    />
                                                </div>
                                            </div>
                                            <Button
                                                variant="outline"
                                                className="w-full"
                                                onClick={
                                                    handleResetConversation
                                                }
                                            >
                                                <RefreshCw className="mr-2 h-4 w-4" />
                                                Reset conversation
                                            </Button>
                                        </div>
                                    </CollapsibleContent>
                                </Collapsible>
                                {/* Conversation History section hidden for now
                                <Separator />
                                <Collapsible defaultOpen={true}>
                                    <div className="flex flex-wrap items-center justify-between gap-2">
                                        <CollapsibleTrigger className="group flex cursor-pointer items-center gap-2 text-left text-sm font-semibold">
                                            <span>Conversation History</span>
                                            <ChevronDown className="h-4 w-4 transition-transform group-data-[state=open]:rotate-180" />
                                        </CollapsibleTrigger>
                                        <div className="flex flex-wrap gap-2">
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() =>
                                                    handleAddMessage('user')
                                                }
                                            >
                                                <Plus className="mr-2 h-3 w-3" />
                                                Add user
                                            </Button>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() =>
                                                    handleAddMessage(
                                                        'assistant',
                                                    )
                                                }
                                            >
                                                <Plus className="mr-2 h-3 w-3" />
                                                Add assistant
                                            </Button>
                                        </div>
                                    </div>
                                    <CollapsibleContent>
                                        <div className="mt-4">
                                            {!selectedMessage ? (
                                                <div className="text-muted-foreground text-sm">
                                                    Select a message from the
                                                    thread to edit it.
                                                </div>
                                            ) : (
                                                <div className="space-y-3">
                                                    <div className="space-y-2">
                                                        <Label>Role</Label>
                                                        <Select
                                                            value={
                                                                selectedMessage.role
                                                            }
                                                            onValueChange={(
                                                                value,
                                                            ) =>
                                                                handleUpdateMessage(
                                                                    selectedMessage.id,
                                                                    {
                                                                        role: value as MessageRole,
                                                                    },
                                                                )
                                                            }
                                                        >
                                                            <SelectTrigger>
                                                                <SelectValue />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                <SelectItem value="user">
                                                                    User
                                                                </SelectItem>
                                                                <SelectItem value="assistant">
                                                                    Assistant
                                                                </SelectItem>
                                                            </SelectContent>
                                                        </Select>
                                                    </div>
                                                    <div className="space-y-2">
                                                        <Label>Timestamp</Label>
                                                        <Input
                                                            value={
                                                                selectedMessage.timestamp
                                                            }
                                                            onChange={(event) =>
                                                                handleUpdateMessage(
                                                                    selectedMessage.id,
                                                                    {
                                                                        timestamp:
                                                                            event
                                                                                .target
                                                                                .value,
                                                                    },
                                                                )
                                                            }
                                                        />
                                                    </div>
                                                    <div className="space-y-2">
                                                        <Label>Body</Label>
                                                        <Textarea
                                                            value={
                                                                selectedMessage.body
                                                            }
                                                            rows={4}
                                                            onChange={(event) =>
                                                                handleUpdateMessage(
                                                                    selectedMessage.id,
                                                                    {
                                                                        body: event
                                                                            .target
                                                                            .value,
                                                                    },
                                                                )
                                                            }
                                                        />
                                                    </div>
                                                    <Collapsible>
                                                        <CollapsibleTrigger
                                                            asChild={true}
                                                        >
                                                            <Button
                                                                variant="ghost"
                                                                className="h-7 px-2 text-xs"
                                                            >
                                                                Domain links
                                                            </Button>
                                                        </CollapsibleTrigger>
                                                        <CollapsibleContent className="mt-2 space-y-2">
                                                            <Textarea
                                                                value={
                                                                    selectedMessage.domainLinksJson
                                                                }
                                                                rows={6}
                                                                onChange={(
                                                                    event,
                                                                ) =>
                                                                    handleUpdateMessage(
                                                                        selectedMessage.id,
                                                                        {
                                                                            domainLinksJson:
                                                                                event
                                                                                    .target
                                                                                    .value,
                                                                        },
                                                                    )
                                                                }
                                                            />
                                                            {domainLinkErrors[
                                                                selectedMessage
                                                                    .id
                                                            ] ? (
                                                                <div className="text-destructive text-xs">
                                                                    {
                                                                        domainLinkErrors[
                                                                            selectedMessage
                                                                                .id
                                                                        ]
                                                                    }
                                                                </div>
                                                            ) : null}
                                                        </CollapsibleContent>
                                                    </Collapsible>
                                                    <Button
                                                        variant="outline"
                                                        onClick={() =>
                                                            handleRemoveMessage(
                                                                selectedMessage.id,
                                                            )
                                                        }
                                                    >
                                                        <Trash2 className="mr-2 h-4 w-4" />
                                                        Remove message
                                                    </Button>
                                                </div>
                                            )}
                                        </div>
                                    </CollapsibleContent>
                                </Collapsible>
                                */}
                                <Separator />
                                <Collapsible defaultOpen={true}>
                                    <CollapsibleTrigger className="group flex w-full cursor-pointer items-center justify-between text-left text-sm font-semibold">
                                        <span>Debug Output</span>
                                        <ChevronDown className="h-4 w-4 transition-transform group-data-[state=open]:rotate-180" />
                                    </CollapsibleTrigger>
                                    <CollapsibleContent>
                                        <div className="mt-4">
                                            {!runResult ? (
                                                <div className="text-muted-foreground text-sm">
                                                    Run a debug turn to see
                                                    output.
                                                </div>
                                            ) : (
                                                <div className="space-y-3">
                                                    <div className="space-y-2">
                                                        <Label>
                                                            Final reply
                                                        </Label>
                                                        <Textarea
                                                            value={
                                                                runResult.final_reply
                                                            }
                                                            readOnly={true}
                                                            rows={3}
                                                        />
                                                    </div>
                                                    <div className="space-y-2">
                                                        <Label>Raw reply</Label>
                                                        <Textarea
                                                            value={
                                                                runResult.raw_reply
                                                            }
                                                            readOnly={true}
                                                            rows={2}
                                                        />
                                                    </div>
                                                    <div className="grid gap-3 sm:grid-cols-2">
                                                        <div className="rounded-md border p-3 text-sm">
                                                            <div className="text-muted-foreground">
                                                                Auto-send
                                                            </div>
                                                            <div className="text-lg font-semibold">
                                                                {runResult.should_auto_send
                                                                    ? 'Yes'
                                                                    : 'No'}
                                                            </div>
                                                        </div>
                                                        <div className="rounded-md border p-3 text-sm">
                                                            <div className="text-muted-foreground">
                                                                Skip response
                                                            </div>
                                                            <div className="text-lg font-semibold">
                                                                {runResult.skip_response
                                                                    ? 'Yes'
                                                                    : 'No'}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="space-y-2">
                                                        <Label>Actions</Label>
                                                        {runResult.actions
                                                            .length === 0 ? (
                                                            <div className="text-muted-foreground text-sm">
                                                                No actions
                                                                returned.
                                                            </div>
                                                        ) : (
                                                            <div className="space-y-2">
                                                                {runResult.actions.map(
                                                                    (
                                                                        action,
                                                                        index,
                                                                    ) => (
                                                                        <div
                                                                            key={`${action.type}-${index}`}
                                                                            className="rounded-md border px-3 py-2 text-sm"
                                                                        >
                                                                            <div className="font-medium">
                                                                                {
                                                                                    action.type
                                                                                }
                                                                            </div>
                                                                            {action.response ? (
                                                                                <div>
                                                                                    Response:{' '}
                                                                                    {
                                                                                        action.response
                                                                                    }
                                                                                </div>
                                                                            ) : null}
                                                                            {action.shift_confirmation_id ? (
                                                                                <div>
                                                                                    Shift
                                                                                    confirmation
                                                                                    ID:{' '}
                                                                                    {
                                                                                        action.shift_confirmation_id
                                                                                    }
                                                                                </div>
                                                                            ) : null}
                                                                            {action.contact_attempt_id ? (
                                                                                <div>
                                                                                    Contact
                                                                                    attempt
                                                                                    ID:{' '}
                                                                                    {
                                                                                        action.contact_attempt_id
                                                                                    }
                                                                                </div>
                                                                            ) : null}
                                                                            {action.reason ? (
                                                                                <div>
                                                                                    Reason:{' '}
                                                                                    {
                                                                                        action.reason
                                                                                    }
                                                                                </div>
                                                                            ) : null}
                                                                            {action.category ? (
                                                                                <div>
                                                                                    Category:{' '}
                                                                                    {
                                                                                        action.category
                                                                                    }
                                                                                </div>
                                                                            ) : null}
                                                                            {action.urgency ? (
                                                                                <div>
                                                                                    Urgency:{' '}
                                                                                    {
                                                                                        action.urgency
                                                                                    }
                                                                                </div>
                                                                            ) : null}
                                                                            {action.summary ? (
                                                                                <div>
                                                                                    Summary:{' '}
                                                                                    {
                                                                                        action.summary
                                                                                    }
                                                                                </div>
                                                                            ) : null}
                                                                            {action.evidence_quote ? (
                                                                                <div>
                                                                                    Evidence:{' '}
                                                                                    {
                                                                                        action.evidence_quote
                                                                                    }
                                                                                </div>
                                                                            ) : null}
                                                                            {action.status ? (
                                                                                <div>
                                                                                    Status:{' '}
                                                                                    {
                                                                                        action.status
                                                                                    }
                                                                                </div>
                                                                            ) : null}
                                                                            {action.scope ? (
                                                                                <div>
                                                                                    Scope:{' '}
                                                                                    {
                                                                                        action.scope
                                                                                    }
                                                                                </div>
                                                                            ) : null}
                                                                            {action.day_of_week ? (
                                                                                <div>
                                                                                    Day:{' '}
                                                                                    {
                                                                                        action.day_of_week
                                                                                    }
                                                                                </div>
                                                                            ) : null}
                                                                            {action.start_time ? (
                                                                                <div>
                                                                                    Start:{' '}
                                                                                    {
                                                                                        action.start_time
                                                                                    }
                                                                                </div>
                                                                            ) : null}
                                                                            {action.end_time ? (
                                                                                <div>
                                                                                    End:{' '}
                                                                                    {
                                                                                        action.end_time
                                                                                    }
                                                                                </div>
                                                                            ) : null}
                                                                            {action.start_minute_of_week !==
                                                                                null &&
                                                                            action.start_minute_of_week !==
                                                                                undefined ? (
                                                                                <div>
                                                                                    Start
                                                                                    minute:{' '}
                                                                                    {
                                                                                        action.start_minute_of_week
                                                                                    }
                                                                                </div>
                                                                            ) : null}
                                                                            {action.end_minute_of_week !==
                                                                                null &&
                                                                            action.end_minute_of_week !==
                                                                                undefined ? (
                                                                                <div>
                                                                                    End
                                                                                    minute:{' '}
                                                                                    {
                                                                                        action.end_minute_of_week
                                                                                    }
                                                                                </div>
                                                                            ) : null}
                                                                            {action.note ? (
                                                                                <div>
                                                                                    Note:{' '}
                                                                                    {
                                                                                        action.note
                                                                                    }
                                                                                </div>
                                                                            ) : null}
                                                                            {action.memory_id ? (
                                                                                <div>
                                                                                    Memory
                                                                                    ID:{' '}
                                                                                    {
                                                                                        action.memory_id
                                                                                    }
                                                                                </div>
                                                                            ) : null}
                                                                            {action.fragment_id ? (
                                                                                <div>
                                                                                    Fragment
                                                                                    ID:{' '}
                                                                                    {
                                                                                        action.fragment_id
                                                                                    }
                                                                                </div>
                                                                            ) : null}
                                                                        </div>
                                                                    ),
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                    {runResult.detected_language ? (
                                                        <div className="text-muted-foreground text-xs">
                                                            Detected language:{' '}
                                                            {
                                                                runResult.detected_language
                                                            }
                                                        </div>
                                                    ) : null}
                                                </div>
                                            )}
                                        </div>
                                    </CollapsibleContent>
                                </Collapsible>
                                <Separator />
                                <Collapsible defaultOpen={true}>
                                    <CollapsibleTrigger className="group flex w-full cursor-pointer items-center justify-between text-left text-sm font-semibold">
                                        <span>Trace and Snapshot</span>
                                        <ChevronDown className="h-4 w-4 transition-transform group-data-[state=open]:rotate-180" />
                                    </CollapsibleTrigger>
                                    <CollapsibleContent>
                                        <div className="mt-4">
                                            {!runResult ? (
                                                <div className="text-muted-foreground text-sm">
                                                    Run a debug turn to see the
                                                    trace.
                                                </div>
                                            ) : (
                                                <React.Fragment>
                                                    <ScrollArea className="h-[320px] rounded-md border">
                                                        <div className="space-y-3 p-4 text-sm">
                                                            {traceItems.map(
                                                                (item) => (
                                                                    <div
                                                                        key={`${item.id}-${item.index_in_turn}`}
                                                                        className="rounded-md border px-3 py-2"
                                                                    >
                                                                        <div className="text-muted-foreground text-xs">
                                                                            {
                                                                                item.type
                                                                            }{' '}
                                                                            -{' '}
                                                                            {item.tool_name ??
                                                                                item.role ??
                                                                                'assistant'}
                                                                        </div>
                                                                        {item.text_content ? (
                                                                            <div className="mt-1 whitespace-pre-wrap">
                                                                                {
                                                                                    item.text_content
                                                                                }
                                                                            </div>
                                                                        ) : null}
                                                                        {item.arguments_json ? (
                                                                            <pre className="bg-muted mt-2 rounded px-2 py-1 text-xs whitespace-pre-wrap">
                                                                                {JSON.stringify(
                                                                                    item.arguments_json,
                                                                                    null,
                                                                                    2,
                                                                                )}
                                                                            </pre>
                                                                        ) : null}
                                                                        {item.output_json ? (
                                                                            <pre className="bg-muted mt-2 rounded px-2 py-1 text-xs whitespace-pre-wrap">
                                                                                {JSON.stringify(
                                                                                    item.output_json,
                                                                                    null,
                                                                                    2,
                                                                                )}
                                                                            </pre>
                                                                        ) : null}
                                                                    </div>
                                                                ),
                                                            )}
                                                        </div>
                                                    </ScrollArea>
                                                    <Collapsible>
                                                        <CollapsibleTrigger
                                                            asChild={true}
                                                        >
                                                            <Button
                                                                variant="outline"
                                                                className="w-full"
                                                            >
                                                                <Copy className="mr-2 h-4 w-4" />
                                                                Context snapshot
                                                                JSON
                                                            </Button>
                                                        </CollapsibleTrigger>
                                                        <CollapsibleContent className="mt-3">
                                                            <Textarea
                                                                value={
                                                                    snapshotJson
                                                                }
                                                                readOnly={true}
                                                                rows={10}
                                                            />
                                                        </CollapsibleContent>
                                                    </Collapsible>
                                                </React.Fragment>
                                            )}
                                        </div>
                                    </CollapsibleContent>
                                </Collapsible>
                            </div>
                        </ScrollArea>
                    </ResizablePanel>
                </ResizablePanelGroup>
            </div>
            <Dialog
                open={domainLinkModalState !== null}
                onOpenChange={(open: boolean) => {
                    if (!open) {
                        setDomainLinkModalState(null);
                    }
                }}
            >
                <DialogContent className="max-h-[85vh] w-[95vw] max-w-4xl overflow-hidden">
                    <DialogHeader>
                        <DialogTitle>Domain links</DialogTitle>
                        <DialogDescription>
                            {domainLinkModalMessage
                                ? `Message ${domainLinkModalMessage.role} · ${domainLinkModalMessage.timestamp}`
                                : 'Inspect domain context links for this message.'}
                        </DialogDescription>
                    </DialogHeader>
                    <ScrollArea className="max-h-[70vh] pr-4">
                        {!domainLinkModalMessage ? (
                            <div className="text-muted-foreground text-sm">
                                Select a message to view its domain links.
                            </div>
                        ) : domainLinkModalLinks.length === 0 ? (
                            <div className="text-muted-foreground text-sm">
                                No domain links attached to this message.
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {domainLinkModalLinks.map((link, index) => {
                                    const isSelected =
                                        domainLinkModalState?.linkIndex ===
                                        index;
                                    const isActive = isDomainLinkActive(
                                        link,
                                        activeDomainHints,
                                    );
                                    return (
                                        <div
                                            key={`${getDomainLinkKey(
                                                link,
                                            )}-${index}`}
                                            className={cn(
                                                'rounded-md border p-3 text-sm',
                                                isSelected &&
                                                    'border-blue-500 shadow-sm',
                                            )}
                                        >
                                            <div className="flex items-center justify-between gap-2">
                                                <div className="font-medium">
                                                    {getDomainLinkSummary(link)}
                                                </div>
                                                {isActive ? (
                                                    <Badge variant="outline">
                                                        Active
                                                    </Badge>
                                                ) : null}
                                            </div>
                                            <pre className="bg-muted mt-2 rounded px-2 py-1 text-xs whitespace-pre-wrap">
                                                {JSON.stringify(link, null, 2)}
                                            </pre>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </ScrollArea>
                </DialogContent>
            </Dialog>
            <Dialog
                open={traceModalMessageId !== null}
                onOpenChange={(open: boolean) => {
                    if (!open) {
                        setTraceModalMessageId(null);
                    }
                }}
            >
                <DialogContent className="max-h-[85vh] w-[95vw] max-w-6xl overflow-hidden">
                    <DialogHeader>
                        <DialogTitle>Run details</DialogTitle>
                        <DialogDescription>
                            {traceModalMessage
                                ? `Message ${traceModalMessage.role} · ${traceModalMessage.timestamp}`
                                : 'Inspect tool calls and reasoning traces.'}
                        </DialogDescription>
                    </DialogHeader>
                    <ScrollArea className="max-h-[70vh] pr-4">
                        {!traceModalRunResult ? (
                            <div className="text-muted-foreground text-sm">
                                No trace data for this message yet.
                            </div>
                        ) : (
                            <div className="space-y-6">
                                <div className="rounded-md border p-3 text-sm">
                                    <div className="text-muted-foreground text-xs">
                                        Message
                                    </div>
                                    <div className="mt-2 whitespace-pre-wrap">
                                        {traceModalMessage?.body ||
                                            traceModalRunResult.final_reply}
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label>Final reply</Label>
                                    <Textarea
                                        value={traceModalRunResult.final_reply}
                                        readOnly={true}
                                        rows={3}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Raw reply</Label>
                                    <Textarea
                                        value={traceModalRunResult.raw_reply}
                                        readOnly={true}
                                        rows={2}
                                    />
                                </div>
                                <div className="grid gap-3 sm:grid-cols-2">
                                    <div className="rounded-md border p-3 text-sm">
                                        <div className="text-muted-foreground">
                                            Auto-send
                                        </div>
                                        <div className="text-lg font-semibold">
                                            {traceModalRunResult.should_auto_send
                                                ? 'Yes'
                                                : 'No'}
                                        </div>
                                    </div>
                                    <div className="rounded-md border p-3 text-sm">
                                        <div className="text-muted-foreground">
                                            Skip response
                                        </div>
                                        <div className="text-lg font-semibold">
                                            {traceModalRunResult.skip_response
                                                ? 'Yes'
                                                : 'No'}
                                        </div>
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label>Tool calls</Label>
                                    {traceModalToolCalls.length === 0 ? (
                                        <div className="text-muted-foreground text-sm">
                                            No tool calls returned.
                                        </div>
                                    ) : (
                                        <div className="space-y-2">
                                            {traceModalToolCalls.map((item) => (
                                                <div
                                                    key={item.id}
                                                    className="rounded-md border px-3 py-2 text-sm"
                                                >
                                                    <div className="font-medium">
                                                        {getToolCallLabel(item)}
                                                    </div>
                                                    {item.arguments_json ? (
                                                        <pre className="bg-muted mt-2 rounded px-2 py-1 text-xs whitespace-pre-wrap">
                                                            {JSON.stringify(
                                                                item.arguments_json,
                                                                null,
                                                                2,
                                                            )}
                                                        </pre>
                                                    ) : null}
                                                    {item.output_json ? (
                                                        <pre className="bg-muted mt-2 rounded px-2 py-1 text-xs whitespace-pre-wrap">
                                                            {JSON.stringify(
                                                                item.output_json,
                                                                null,
                                                                2,
                                                            )}
                                                        </pre>
                                                    ) : null}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <div className="space-y-2">
                                    <Label>Reasoning traces</Label>
                                    {traceModalReasoningItems.length === 0 ? (
                                        <div className="text-muted-foreground text-sm">
                                            No reasoning traces returned.
                                        </div>
                                    ) : (
                                        <div className="space-y-2">
                                            {traceModalReasoningItems.map(
                                                (item) => (
                                                    <div
                                                        key={item.id}
                                                        className="rounded-md border px-3 py-2 text-sm whitespace-pre-wrap"
                                                    >
                                                        {item.text_content ??
                                                            ''}
                                                    </div>
                                                ),
                                            )}
                                        </div>
                                    )}
                                </div>
                                <div className="space-y-2">
                                    <Label>Trace items</Label>
                                    <ScrollArea className="h-[320px] rounded-md border">
                                        <div className="space-y-3 p-4 text-sm">
                                            {traceModalItems.map((item) => (
                                                <div
                                                    key={`${item.id}-${item.index_in_turn}`}
                                                    className="rounded-md border px-3 py-2"
                                                >
                                                    <div className="text-muted-foreground text-xs">
                                                        {item.type} -{' '}
                                                        {item.tool_name ??
                                                            item.role ??
                                                            'assistant'}
                                                    </div>
                                                    {item.text_content ? (
                                                        <div className="mt-1 whitespace-pre-wrap">
                                                            {item.text_content}
                                                        </div>
                                                    ) : null}
                                                    {item.arguments_json ? (
                                                        <pre className="bg-muted mt-2 rounded px-2 py-1 text-xs whitespace-pre-wrap">
                                                            {JSON.stringify(
                                                                item.arguments_json,
                                                                null,
                                                                2,
                                                            )}
                                                        </pre>
                                                    ) : null}
                                                    {item.output_json ? (
                                                        <pre className="bg-muted mt-2 rounded px-2 py-1 text-xs whitespace-pre-wrap">
                                                            {JSON.stringify(
                                                                item.output_json,
                                                                null,
                                                                2,
                                                            )}
                                                        </pre>
                                                    ) : null}
                                                </div>
                                            ))}
                                        </div>
                                    </ScrollArea>
                                </div>
                                <Collapsible>
                                    <CollapsibleTrigger asChild={true}>
                                        <Button
                                            variant="outline"
                                            className="w-full"
                                        >
                                            <Copy className="mr-2 h-4 w-4" />
                                            Context snapshot JSON
                                        </Button>
                                    </CollapsibleTrigger>
                                    <CollapsibleContent className="mt-3">
                                        <Textarea
                                            value={traceModalSnapshotJson}
                                            readOnly={true}
                                            rows={10}
                                        />
                                    </CollapsibleContent>
                                </Collapsible>
                            </div>
                        )}
                    </ScrollArea>
                </DialogContent>
            </Dialog>
        </div>
    );
}

const UUID_PATTERN =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isUuid(value: string) {
    return UUID_PATTERN.test(value.trim());
}

function truncateId(value: string) {
    if (value.length <= 12) {
        return value;
    }
    return `${value.slice(0, 8)}…${value.slice(-4)}`;
}

function parseDomainLinksForDisplay(raw: string): Array<DomainLinkLike> {
    if (!raw.trim()) {
        return [];
    }
    try {
        const parsed = JSON.parse(raw) as unknown;
        if (!Array.isArray(parsed)) {
            return [];
        }
        return parsed as Array<DomainLinkLike>;
    } catch (error) {
        return [];
    }
}

function getDomainLinkKind(
    link: DomainLinkLike,
): SmsDebugDomainHints['primary_domain_type'] | null {
    if (link.type === 'shift_confirmation') {
        return 'shift_confirmation';
    }
    if (link.type === 'callout_contact_attempt' || link.type === 'callout') {
        return 'callout';
    }
    if (link.shift_confirmation_id) {
        return 'shift_confirmation';
    }
    if (link.callout_id) {
        return 'callout';
    }
    return null;
}

function getDomainLinkPrimaryId(link: DomainLinkLike): string | null {
    const kind = getDomainLinkKind(link);
    if (kind === 'shift_confirmation') {
        return link.shift_confirmation_id ?? null;
    }
    if (kind === 'callout') {
        return link.callout_id ?? null;
    }
    return null;
}

function getDomainLinkKey(link: DomainLinkLike) {
    const kind = getDomainLinkKind(link) ?? 'domain_link';
    const id = getDomainLinkPrimaryId(link) ?? 'unknown';
    return `${kind}:${id}`;
}

function getDomainLinkSummary(link: DomainLinkLike) {
    const kind = getDomainLinkKind(link);
    const baseLabel =
        kind === 'shift_confirmation'
            ? 'Shift confirmation'
            : kind === 'callout'
              ? 'Open Shift'
              : 'Domain link';

    const detailParts: Array<string> = [];
    if (link.shift_date) {
        detailParts.push(link.shift_date);
    }
    if (link.patient_name) {
        detailParts.push(link.patient_name);
    }
    if (kind === 'callout' && link.caregiver_first_name) {
        detailParts.push(link.caregiver_first_name);
    }
    const detailLabel = detailParts.slice(0, 2).join(' • ');
    if (detailLabel) {
        return `${baseLabel} • ${detailLabel}`;
    }
    const id = getDomainLinkPrimaryId(link);
    if (id) {
        return `${baseLabel} • ${truncateId(id)}`;
    }
    return baseLabel;
}

function isDomainLinkActive(
    link: DomainLinkLike,
    hints: SmsDebugDomainHints | null,
) {
    if (!hints?.primary_domain_type || !hints.primary_domain_id) {
        return false;
    }
    const kind = getDomainLinkKind(link);
    const id = getDomainLinkPrimaryId(link);
    if (!kind || !id) {
        return false;
    }
    return kind === hints.primary_domain_type && id === hints.primary_domain_id;
}

function splitFullName(fullName?: string | null) {
    const trimmed = fullName?.trim() ?? '';
    if (!trimmed) {
        return { firstName: '', lastName: '' };
    }
    const [firstName = '', ...rest] = trimmed.split(/\s+/);
    return { firstName, lastName: rest.join(' ') };
}

function getOrganizationLabel(organization: AdminOrganizationOption) {
    const internalName = organization.internal_name?.trim();
    if (internalName && internalName.length > 0) {
        return internalName;
    }
    return organization.name;
}

function formatTemplate(template: string, values: Record<string, string>) {
    let result = template;
    for (const [key, value] of Object.entries(values)) {
        result = result.replace(new RegExp(`\\{${key}\\}`, 'g'), () => value);
    }
    return result;
}

function buildDefaultMessageForLink(link: DomainLinkLike) {
    if (link.type === 'shift_confirmation') {
        const organizationName = link.agency_name ?? 'our agency';
        const shiftDate = link.shift_date ?? 'your upcoming shift';
        const patientName = link.patient_name ?? 'the patient';
        const patientAddress =
            link.patient_address ??
            link.shift_location ??
            'the patient address';
        return formatTemplate(DEFAULT_SHIFT_CONFIRMATION_TEMPLATE, {
            organization_name: organizationName,
            shift_date: shiftDate,
            patient_name: patientName,
            patient_address: patientAddress,
        });
    }

    if (link.type === 'callout_contact_attempt') {
        const caregiverFirstName = link.caregiver_first_name ?? 'there';
        const shiftDate = link.shift_date ?? 'your upcoming shift';
        const startTime = link.shift_start_time ?? 'TBD';
        const endTime = link.shift_end_time ?? 'TBD';
        const city = link.shift_city?.trim();
        const locationText = city ? ` in ${city}` : '';
        return formatTemplate(DEFAULT_CALLOUT_TEMPLATE, {
            caregiver_first_name: caregiverFirstName,
            shift_date: shiftDate,
            start_time: startTime,
            end_time: endTime,
            location_text: locationText,
        });
    }

    return '';
}

function buildDefaultMessageForLinks(links: Array<DomainLinkLike>) {
    const shiftConfirmationLinks = links.filter(
        (link) => link.type === 'shift_confirmation',
    );

    if (shiftConfirmationLinks.length > 1) {
        const firstLink = shiftConfirmationLinks[0] as DomainLinkLike;
        const organizationName = firstLink.agency_name ?? 'our agency';
        let message = formatTemplate(
            DEFAULT_MULTI_SHIFT_CONFIRMATION_TEMPLATE,
            {
                organization_name: organizationName,
            },
        );

        for (const link of shiftConfirmationLinks) {
            const shiftDate = link.shift_date ?? 'TBD';
            const patientName = link.patient_name ?? 'the patient';
            const patientAddress =
                link.patient_address ?? link.shift_location ?? 'TBD';
            message +=
                '\n' +
                formatTemplate(DEFAULT_MULTI_SHIFT_BULLET_TEMPLATE, {
                    shift_date: shiftDate,
                    patient_name: patientName,
                    patient_address: patientAddress,
                });
        }

        return message;
    }

    for (const link of links) {
        const message = buildDefaultMessageForLink(link);
        if (message) {
            return message;
        }
    }
    return '';
}

function getToolCallItems(items: Array<SmsDebugItemRead>) {
    return items.filter((item) => Boolean(item.tool_name));
}

function getReasoningItems(items: Array<SmsDebugItemRead>) {
    return items.filter((item) => item.type === 'reasoning');
}

function getToolCallLabel(item: SmsDebugItemRead) {
    const toolName = item.tool_name ?? 'Tool call';

    if (!item.arguments_json) {
        return toolName;
    }

    const args = item.arguments_json as Record<string, unknown>;
    const keys = Object.keys(args);

    if (keys.length === 0) {
        return toolName;
    }

    const firstKey = keys[0] as string;
    const firstValue = args[firstKey];
    let valueStr: string;

    if (typeof firstValue === 'string') {
        valueStr =
            firstValue.length > 20
                ? `"${firstValue.slice(0, 20)}..."`
                : `"${firstValue}"`;
    } else {
        const json = JSON.stringify(firstValue);
        valueStr = json.length > 25 ? `${json.slice(0, 25)}...` : json;
    }

    return `${toolName}(${firstKey}: ${valueStr})`;
}

function SmsDebugOrganizationCombobox({
    value,
    onValueChange,
}: {
    readonly value: string;
    readonly onValueChange: (value: string) => void;
}) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebouncedValue(search.trim(), 300);
    const deferredSearch = useDeferredValue(debouncedSearch);

    const { data: organizationsResponse, isLoading } = useAdminOrganizations({
        ...(deferredSearch && { search: deferredSearch }),
        page: 1,
        per_page: 50,
    });

    const { data: selectedOrganization } = useAdminOrganization(value || null);

    const organizations = useMemo(
        () => organizationsResponse?.organizations ?? [],
        [organizationsResponse],
    );
    const selectedRecord =
        organizations.find((org) => org.id === value) ??
        selectedOrganization ??
        null;

    const label = selectedRecord ? getOrganizationLabel(selectedRecord) : '';
    const displayValue = label || (value ? truncateId(value) : undefined);

    return (
        <Combobox
            open={open}
            onOpenChange={setOpen}
            displayValue={displayValue}
            placeholder="Select an organization..."
            disabled={isLoading && organizations.length === 0}
            searchValue={search}
            onSearchValueChange={setSearch}
            searchPlaceholder="Search by name or ID..."
            commandProps={{ shouldFilter: false }}
            contentClassName="w-[360px]"
            matchTriggerWidth={false}
        >
            <CommandEmpty>
                {isLoading
                    ? 'Loading organizations…'
                    : 'No organizations found.'}
            </CommandEmpty>
            <CommandGroup>
                {organizations.map((org) => {
                    const orgLabel = getOrganizationLabel(org);
                    const internalName = org.internal_name?.trim();
                    const showSecondaryName =
                        internalName &&
                        internalName.length > 0 &&
                        internalName !== org.name;

                    const keywords = [
                        org.id,
                        org.name,
                        internalName ?? '',
                    ].filter(Boolean);

                    return (
                        <CommandItem
                            key={org.id}
                            value={org.id}
                            keywords={keywords}
                            onSelect={(currentValue) => {
                                onValueChange(currentValue);
                                setOpen(false);
                            }}
                        >
                            <Check
                                className={cn(
                                    'mr-2 h-4 w-4',
                                    value === org.id
                                        ? 'opacity-100'
                                        : 'opacity-0',
                                )}
                            />
                            <div className="flex min-w-0 flex-col">
                                <span className="truncate">{orgLabel}</span>
                                {showSecondaryName && (
                                    <span className="text-muted-foreground truncate text-xs">
                                        {org.name}
                                    </span>
                                )}
                            </div>
                        </CommandItem>
                    );
                })}
            </CommandGroup>
        </Combobox>
    );
}

function SmsDebugCaregiverCombobox({
    value,
    organizationId,
    onSelect,
}: {
    readonly value: string;
    readonly organizationId: string;
    readonly onSelect: (
        record: AdminCaregiverOption | null,
        nextValue: string,
    ) => void;
}) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebouncedValue(search.trim(), 300);
    const deferredSearch = useDeferredValue(debouncedSearch);

    const { data: caregiversResponse, isLoading } = useAdminCaregivers({
        ...(organizationId && { organization_id: organizationId }),
        status: 'active',
        ...(deferredSearch && { search: deferredSearch }),
        per_page: 50,
    });

    const { data: selectedCaregiver } = useAdminCaregiver(value || null);

    const caregivers = useMemo(
        () => caregiversResponse?.caregivers ?? [],
        [caregiversResponse],
    );
    const selectedRecord =
        caregivers.find((caregiver) => caregiver.id === value) ??
        selectedCaregiver ??
        null;

    const displayValue =
        selectedRecord?.name ?? (value ? truncateId(value) : undefined);
    const rawSearch = search.trim();
    const showCustomIdOption =
        isUuid(rawSearch) &&
        rawSearch !== value &&
        !caregivers.some((caregiver) => caregiver.id === rawSearch);

    return (
        <Combobox
            open={open}
            onOpenChange={setOpen}
            displayValue={displayValue}
            placeholder="Select a caregiver..."
            disabled={isLoading && caregivers.length === 0}
            searchValue={search}
            onSearchValueChange={setSearch}
            searchPlaceholder="Search by name or ID..."
            commandProps={{ shouldFilter: false }}
            contentClassName="w-[360px]"
            matchTriggerWidth={false}
        >
            <CommandEmpty>
                {isLoading ? 'Loading caregivers…' : 'No caregivers found.'}
            </CommandEmpty>
            <CommandGroup>
                {showCustomIdOption && (
                    <CommandItem
                        value={rawSearch}
                        onSelect={() => {
                            onSelect(null, rawSearch);
                            setOpen(false);
                        }}
                    >
                        Use custom ID {truncateId(rawSearch)}
                    </CommandItem>
                )}
                {caregivers.map((caregiver) => {
                    const keywords = [
                        caregiver.id,
                        caregiver.name,
                        caregiver.organization_name ?? '',
                        caregiver.first_name ?? '',
                        caregiver.last_name ?? '',
                        caregiver.preferred_name ?? '',
                    ].filter(Boolean);

                    return (
                        <CommandItem
                            key={caregiver.id}
                            value={caregiver.id}
                            keywords={keywords}
                            onSelect={(currentValue) => {
                                onSelect(caregiver, currentValue);
                                setOpen(false);
                            }}
                        >
                            <Check
                                className={cn(
                                    'mr-2 h-4 w-4',
                                    value === caregiver.id
                                        ? 'opacity-100'
                                        : 'opacity-0',
                                )}
                            />
                            <div className="flex flex-col">
                                <span>{caregiver.name}</span>
                                {!organizationId &&
                                    caregiver.organization_name && (
                                        <span className="text-muted-foreground text-xs">
                                            {caregiver.organization_name}
                                        </span>
                                    )}
                            </div>
                        </CommandItem>
                    );
                })}
            </CommandGroup>
        </Combobox>
    );
}

function SmsDebugContactCombobox({
    value,
    organizationId,
    caregiverId,
    filterByCaregiver,
    onSelect,
}: {
    readonly value: string;
    readonly organizationId: string;
    readonly caregiverId: string;
    readonly filterByCaregiver: boolean;
    readonly onSelect: (
        record: SmsDebugContactOption | null,
        nextValue: string,
    ) => void;
}) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const rawSearch = search.trim();
    const debouncedSearch = useDebouncedValue(rawSearch, 300);
    const deferredSearch = useDeferredValue(debouncedSearch);
    const MIN_SEARCH_LENGTH = 2;
    const activeSearchTerm =
        deferredSearch.length >= MIN_SEARCH_LENGTH ? deferredSearch : '';
    const activeCaregiverId = filterByCaregiver ? caregiverId : '';

    const { data: contactsResponse, isLoading: isSearching } =
        useAdminSmsDebugContacts(
            {
                ...(organizationId && { organization_id: organizationId }),
                ...(activeCaregiverId && {
                    caregiver_id: activeCaregiverId,
                }),
                ...(activeSearchTerm && { search: activeSearchTerm }),
                page: 1,
                per_page: 25,
            },
            { enabled: Boolean(activeSearchTerm) },
        );

    const { data: selectedContactResponse } = useAdminSmsDebugContacts(
        value
            ? {
                  search: value,
                  page: 1,
                  per_page: 1,
              }
            : undefined,
        { enabled: Boolean(value) },
    );

    const contacts = useMemo(
        () => contactsResponse?.contacts ?? [],
        [contactsResponse],
    );
    const selectedRecord =
        contacts.find((contact) => contact.id === value) ??
        selectedContactResponse?.contacts?.find(
            (contact) => contact.id === value,
        ) ??
        null;

    const displayValue =
        (selectedRecord?.caregiver_name ?? selectedRecord?.address) ||
        (value ? truncateId(value) : undefined);

    const showCustomIdOption =
        isUuid(rawSearch) &&
        rawSearch !== value &&
        !contacts.some((contact) => contact.id === rawSearch);

    const emptyMessage =
        rawSearch.length < MIN_SEARCH_LENGTH
            ? `Type at least ${MIN_SEARCH_LENGTH} characters to search.`
            : 'No contacts found.';

    return (
        <Combobox
            open={open}
            onOpenChange={setOpen}
            displayValue={displayValue}
            placeholder="Select a contact..."
            searchValue={search}
            onSearchValueChange={setSearch}
            searchPlaceholder="Search by caregiver, phone, or ID..."
            commandProps={{ shouldFilter: false }}
            contentClassName="w-[380px]"
            matchTriggerWidth={false}
        >
            <CommandEmpty>
                {isSearching ? 'Searching contacts…' : emptyMessage}
            </CommandEmpty>
            <CommandGroup>
                {showCustomIdOption && (
                    <CommandItem
                        value={rawSearch}
                        onSelect={() => {
                            onSelect(null, rawSearch);
                            setOpen(false);
                        }}
                    >
                        Use custom ID {truncateId(rawSearch)}
                    </CommandItem>
                )}
                {contacts.map((contact) => {
                    const primaryLabel =
                        contact.caregiver_name ?? contact.address;
                    const secondaryLabel = contact.caregiver_name
                        ? contact.address
                        : contact.normalized_address;
                    const keywords = [
                        contact.id,
                        contact.address,
                        contact.normalized_address,
                        contact.caregiver_name ?? '',
                    ].filter(Boolean);

                    return (
                        <CommandItem
                            key={contact.id}
                            value={contact.id}
                            keywords={keywords}
                            onSelect={(currentValue) => {
                                onSelect(contact, currentValue);
                                setOpen(false);
                            }}
                        >
                            <Check
                                className={cn(
                                    'mr-2 h-4 w-4',
                                    value === contact.id
                                        ? 'opacity-100'
                                        : 'opacity-0',
                                )}
                            />
                            <div className="flex min-w-0 flex-col">
                                <span className="truncate">{primaryLabel}</span>
                                <span className="text-muted-foreground truncate text-xs">
                                    {secondaryLabel} • {contact.type} •{' '}
                                    {truncateId(contact.id)}
                                </span>
                            </div>
                        </CommandItem>
                    );
                })}
            </CommandGroup>
        </Combobox>
    );
}

function SmsDebugConversationCombobox({
    value,
    organizationId,
    onSelect,
}: {
    readonly value: string;
    readonly organizationId: string;
    readonly onSelect: (
        record: SmsDebugConversationOption | null,
        nextValue: string,
    ) => void;
}) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const rawSearch = search.trim();
    const debouncedSearch = useDebouncedValue(rawSearch, 300);
    const deferredSearch = useDeferredValue(debouncedSearch);
    const MIN_SEARCH_LENGTH = 2;
    const activeSearchTerm =
        deferredSearch.length >= MIN_SEARCH_LENGTH ? deferredSearch : '';

    const { data: conversationsResponse, isLoading: isSearching } =
        useAdminSmsDebugConversations(
            {
                ...(organizationId && { organization_id: organizationId }),
                ...(activeSearchTerm && { search: activeSearchTerm }),
                page: 1,
                per_page: 25,
            },
            { enabled: Boolean(activeSearchTerm) },
        );

    const { data: selectedConversationResponse } =
        useAdminSmsDebugConversations(
            value
                ? {
                      search: value,
                      page: 1,
                      per_page: 1,
                  }
                : undefined,
            { enabled: Boolean(value) },
        );

    const conversations = useMemo(
        () => conversationsResponse?.conversations ?? [],
        [conversationsResponse],
    );
    const selectedRecord =
        conversations.find((conversation) => conversation.id === value) ??
        selectedConversationResponse?.conversations?.find(
            (conversation) => conversation.id === value,
        ) ??
        null;

    const displayValue =
        (selectedRecord?.caregiver_name ?? selectedRecord?.contact_address) ||
        (value ? truncateId(value) : undefined);

    const showCustomIdOption =
        isUuid(rawSearch) &&
        rawSearch !== value &&
        !conversations.some((conversation) => conversation.id === rawSearch);

    const emptyMessage =
        rawSearch.length < MIN_SEARCH_LENGTH
            ? `Type at least ${MIN_SEARCH_LENGTH} characters to search.`
            : 'No conversations found.';

    return (
        <Combobox
            open={open}
            onOpenChange={setOpen}
            displayValue={displayValue}
            placeholder="Select a conversation..."
            searchValue={search}
            onSearchValueChange={setSearch}
            searchPlaceholder="Search by caregiver, phone, or ID..."
            commandProps={{ shouldFilter: false }}
            contentClassName="w-[380px]"
            matchTriggerWidth={false}
        >
            <CommandEmpty>
                {isSearching ? 'Searching conversations…' : emptyMessage}
            </CommandEmpty>
            <CommandGroup>
                {showCustomIdOption && (
                    <CommandItem
                        value={rawSearch}
                        onSelect={() => {
                            onSelect(null, rawSearch);
                            setOpen(false);
                        }}
                    >
                        Use custom ID {truncateId(rawSearch)}
                    </CommandItem>
                )}
                {conversations.map((conversation) => {
                    const primaryLabel =
                        conversation.caregiver_name ??
                        conversation.contact_address;
                    const secondaryLabel = `${conversation.contact_address} • ${truncateId(
                        conversation.id,
                    )}`;
                    const keywords = [
                        conversation.id,
                        conversation.contact_id,
                        conversation.contact_address,
                        conversation.contact_normalized_address,
                        conversation.caregiver_name ?? '',
                    ].filter(Boolean);

                    return (
                        <CommandItem
                            key={conversation.id}
                            value={conversation.id}
                            keywords={keywords}
                            onSelect={(currentValue) => {
                                onSelect(conversation, currentValue);
                                setOpen(false);
                            }}
                        >
                            <Check
                                className={cn(
                                    'mr-2 h-4 w-4',
                                    value === conversation.id
                                        ? 'opacity-100'
                                        : 'opacity-0',
                                )}
                            />
                            <div className="flex min-w-0 flex-col">
                                <span className="truncate">{primaryLabel}</span>
                                <span className="text-muted-foreground truncate text-xs">
                                    {secondaryLabel}
                                </span>
                            </div>
                        </CommandItem>
                    );
                })}
            </CommandGroup>
        </Combobox>
    );
}
